import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './tmi.css';
import './Packlists.css';
import './MasterUsers.css';
import './UserRoles.css';
import { connect, ConnectedProps } from 'react-redux';
import { mapStateToProps, mapDispatchToProps } from '../../redux/reduxActions';
import { UserInfo } from '../../models/models';
import { Grid, GridCellProps, GridColumn } from '@progress/kendo-react-grid';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import ProjectSelectionDialog from './ProjectSelectionDialog';

const MENU_STRUCTURE_CHANGED_EVENT = 'tmi:menus-structure-changed';

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
type UserRolesProps = PropsFromRedux & {
    menuItemId?: number;
    stateToLoad?: UserRolesState;
    onSaveState?: (menuItemId: number, state: UserRolesState) => void;
};

interface UserRoleRow {
    roleKey: string;
    value: string;
    display: string;
}

interface LookupListItem {
    value: string;
    display: string;
}

interface UsersByRoleRow extends LookupListItem {
    clientRowKey: string;
}

interface MenuNodeRow {
    securityID: number;
    parentSecurityID: number | null;
    securityLabel: string;
    description: string;
    sortOrder: number;
    enable: boolean;
}

interface SecurityDetailRow {
    securityDetailID: string;
    parentSecurityDetailID: string | null;
    securityLabel: string;
    securityDescription: string;
    allowEdit: boolean;
}

interface SecurityDetailTreeNode {
    node: SecurityDetailRow;
    children: SecurityDetailTreeNode[];
}

interface TreeNode {
    node: MenuNodeRow;
    children: TreeNode[];
}

enum SecurityDetailDialogMode {
    ItemType = 'ItemType',
    PacklistType = 'PacklistType',
}

const collectSecurityIds = (nodes: TreeNode[]): Set<number> => {
    const ids = new Set<number>();
    const walk = (list: TreeNode[]) => {
        list.forEach(node => {
            ids.add(node.node.securityID);
            if (node.children.length > 0) {
                walk(node.children);
            }
        });
    };
    walk(nodes);
    return ids;
};

const USER_ROLES_BUTTON_HEADER_STYLE: React.CSSProperties = {
    textAlign: 'center',
    verticalAlign: 'middle',
    padding: '2px',
};

const USER_ROLES_HEADER_CELL_INNER_STYLE: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 6px',
    height: '100%',
    boxSizing: 'border-box',
};

interface UserRolesState {
    rolesData: UserRoleRow[];
    menusData: MenuNodeRow[];
    usersByRoleData: UsersByRoleRow[];
    loadingRoles: boolean;
    loadingMenus: boolean;
    loadingUsersByRole: boolean;
    errorMsg: string;
    sort: SortDescriptor[];
    filterTexts: { [key: string]: string };
    headerFocusField: string | null;
    selectedRoleKey: string | null;
    editRoleKey: string | null;
    originalItem: UserRoleRow | null;
    deleteConfirmItem: UserRoleRow | null;
    menuDataItem: UserRoleRow | null;
    menuAnchor: HTMLElement | null;
    menuPosition: { left: number; top: number } | null;
    copyToProjectDialogOpen: boolean;
    copyToProjectRole: UserRoleRow | null;
    copyToProjectTarget: LookupListItem | null;
    copyToProjectName: string;
    copyToProjectNameDialogOpen: boolean;
    copyToProjectDuplicateRole: LookupListItem | null;
    copyToProjectConfirmOpen: boolean;
    copyToProjectError: string;
    newDialogOpen: boolean;
    newDialogDisplay: string;
    newDialogError: string;
    checkedMenuIds: Set<number>;
    collapsedMenuIds: Set<number>;
    usersByRoleSort: SortDescriptor[];
    usersByRoleFilterTexts: { [key: string]: string };
    usersByRoleHeaderFocusField: string | null;
    selectedUserByRoleKey: string | null;
    usersDeleteConfirmItem: UsersByRoleRow | null;
    usersAddDialogOpen: boolean;
    usersAddDialogLoading: boolean;
    usersAddDialogSaving: boolean;
    usersAddDialogFilterText: string;
    usersAddDialogData: LookupListItem[];
    usersAddDialogSelected: Set<string>;
    usersAddDialogError: string;
    securityDetailDialogOpen: boolean;
    securityDetailDialogMode: SecurityDetailDialogMode | null;
    securityDetailDialogTitle: string;
    securityDetailDialogLoading: boolean;
    securityDetailDialogError: string;
    securityDetailDialogStatus: { type: 'success' | 'error'; message: string } | null;
    securityDetailCopying: boolean;
    securityDetailData: SecurityDetailRow[];
    securityDetailAllowAccessMap: { [key: string]: boolean };
    securityDetailAllowEditMap: { [key: string]: boolean };
    securityDetailListLoading: boolean;
    securityDetailListData: LookupListItem[];
    securityDetailListSelected: Set<string>;
    securityDetailListSelectedRow: string | null;
    securityDetailListFilterText: string;
    securityDetailSourceRoleDialogOpen: boolean;
    securityDetailSourceRoleFilterText: string;
    securityDetailSourceRoleSelected: string | null;
}

const normalizeUserRole = (item: any, index: number): UserRoleRow => {
    const valueRaw = item.value ?? item.Value ?? item.userRoleID ?? item.UserRoleID ?? item.id ?? item.ID ?? '';
    const displayRaw = item.display ?? item.Display ?? item.description ?? item.Description ?? item.name ?? item.Name ?? '';
    const value = String(valueRaw ?? '').trim();
    const display = String(displayRaw ?? '').trim();

    return {
        roleKey: value.length > 0 ? value : `role_${index}`,
        value,
        display,
    };
};

const normalizeMenu = (item: any): MenuNodeRow => {
    const securityIdRaw = item.securityID ?? item.SecurityID ?? 0;
    const parentSecurityIdRaw = item.parentSecurityID ?? item.ParentSecurityID ?? null;
    const sortOrderRaw = item.sortOrder ?? item.SortOrder ?? 0;
    const securityID = Number(securityIdRaw);
    const parentSecurityIDCandidate = parentSecurityIdRaw === null || parentSecurityIdRaw === ''
        ? null
        : Number(parentSecurityIdRaw);

    return {
        securityID: Number.isFinite(securityID) ? securityID : 0,
        parentSecurityID: parentSecurityIDCandidate === null || !Number.isFinite(parentSecurityIDCandidate)
            ? null
            : parentSecurityIDCandidate,
        securityLabel: String(item.securityLabel ?? item.SecurityLabel ?? '').trim(),
        description: String(item.description ?? item.Description ?? '').trim(),
        sortOrder: Number.isFinite(Number(sortOrderRaw)) ? Number(sortOrderRaw) : 0,
        enable: Boolean(item.enable ?? item.Enable ?? false),
    };
};

const sortMenus = (a: MenuNodeRow, b: MenuNodeRow): number => {
    if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
    }

    const aLabel = (a.securityLabel || a.description || '').toLowerCase();
    const bLabel = (b.securityLabel || b.description || '').toLowerCase();
    if (aLabel < bLabel) return -1;
    if (aLabel > bLabel) return 1;
    return a.securityID - b.securityID;
};

const buildMenuTree = (menus: MenuNodeRow[]): TreeNode[] => {
    const byId = new Map<number, MenuNodeRow>();
    menus.forEach(m => byId.set(m.securityID, m));

    const childrenByParent = new Map<number | null, MenuNodeRow[]>();
    const addChild = (parentId: number | null, node: MenuNodeRow) => {
        const existing = childrenByParent.get(parentId) ?? [];
        existing.push(node);
        childrenByParent.set(parentId, existing);
    };

    menus.forEach(m => {
        const parent = m.parentSecurityID;
        if (parent !== null && byId.has(parent)) {
            addChild(parent, m);
            return;
        }
        addChild(null, m);
    });

    childrenByParent.forEach(list => list.sort(sortMenus));

    const createNode = (row: MenuNodeRow, path: Set<number>): TreeNode => {
        if (path.has(row.securityID)) {
            return { node: row, children: [] };
        }

        const nextPath = new Set(path);
        nextPath.add(row.securityID);
        const children = (childrenByParent.get(row.securityID) ?? []).map(child => createNode(child, nextPath));
        return { node: row, children };
    };

    return (childrenByParent.get(null) ?? []).map(root => createNode(root, new Set<number>()));
};

const sortSecurityDetailRows = (a: SecurityDetailRow, b: SecurityDetailRow): number => {
    const aText = (a.securityDescription || a.securityLabel || '').trim().toLowerCase();
    const bText = (b.securityDescription || b.securityLabel || '').trim().toLowerCase();
    if (aText < bText) return -1;
    if (aText > bText) return 1;
    return a.securityDetailID.localeCompare(b.securityDetailID);
};

const buildSecurityDetailTree = (items: SecurityDetailRow[]): SecurityDetailTreeNode[] => {
    const byId = new Map<string, SecurityDetailRow>();
    items.forEach(item => byId.set(item.securityDetailID, item));

    const childrenByParent = new Map<string | null, SecurityDetailRow[]>();
    const addChild = (parentId: string | null, node: SecurityDetailRow) => {
        const existing = childrenByParent.get(parentId) ?? [];
        existing.push(node);
        childrenByParent.set(parentId, existing);
    };

    items.forEach(item => {
        const parentId = item.parentSecurityDetailID ?? null;
        if (parentId && byId.has(parentId)) {
            addChild(parentId, item);
            return;
        }
        addChild(null, item);
    });

    const createNode = (row: SecurityDetailRow, path: Set<string>): SecurityDetailTreeNode => {
        if (path.has(row.securityDetailID)) {
            return { node: row, children: [] };
        }

        const nextPath = new Set(path);
        nextPath.add(row.securityDetailID);
        const children = (childrenByParent.get(row.securityDetailID) ?? [])
            .sort(sortSecurityDetailRows)
            .map(child => createNode(child, nextPath));
        return { node: row, children };
    };

    return (childrenByParent.get(null) ?? [])
        .sort(sortSecurityDetailRows)
        .map(root => createNode(root, new Set<string>()));
};

const filterSecurityDetailRowsByMode = (rows: SecurityDetailRow[], mode: SecurityDetailDialogMode): SecurityDetailRow[] => {
    const allowedRoots = new Set<string>();
    const targets = mode === SecurityDetailDialogMode.ItemType
        ? ['Allow Items Details Popup', 'Allow Pack List Item Qty']
        : ['Allow PL Details Popup', 'Allow PLI Details Popup'];

    rows.forEach(row => {
        const label = (row.securityDescription || row.securityLabel || '').trim().toLowerCase();
        if (targets.some(target => label === target.trim().toLowerCase())) {
            allowedRoots.add(row.securityDetailID);
        }
    });

    if (allowedRoots.size === 0) {
        return rows;
    }

    const includeId = new Set<string>();
    const collect = (id: string) => {
        const row = rows.find(item => item.securityDetailID === id);
        if (!row || includeId.has(id)) {
            return;
        }

        includeId.add(id);
        const children = rows.filter(item => (item.parentSecurityDetailID ?? null) === id);
        children.forEach(child => collect(child.securityDetailID));
    };

    allowedRoots.forEach(rootId => collect(rootId));
    return rows.filter(row => includeId.has(row.securityDetailID));
};

const renderSecurityDetailNode = (
    node: SecurityDetailTreeNode,
    depth: number,
    expanded: Set<string>,
    onToggle: (id: string) => void,
    allowAccessMap: { [key: string]: boolean },
    allowEditMap: { [key: string]: boolean },
    onAllowAccessChange: (id: string, value: boolean) => void,
    onAllowEditChange: (id: string, value: boolean) => void,
): React.ReactNode => {
    const description = node.node.securityDescription || 'Security Detail';
    const hasChildren = node.children.length > 0;
    const isExpanded = expanded.has(node.node.securityDetailID);
    const allowAccess = Boolean(allowAccessMap[node.node.securityDetailID]);
    const allowEdit = Boolean(allowEditMap[node.node.securityDetailID]);

    return (
        <div key={node.node.securityDetailID} style={{ marginLeft: depth * 18 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto auto', alignItems: 'center', gap: '6px', padding: '2px 0', borderBottom: '1px solid #f0f0f0' }}>
                {hasChildren ? (
                    <button
                        type="button"
                        onClick={() => onToggle(node.node.securityDetailID)}
                        style={{
                            width: '18px',
                            height: '18px',
                            border: '1px solid #d0d0d0',
                            borderRadius: '3px',
                            background: '#fff',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px',
                            color: '#333',
                            lineHeight: 1,
                            padding: 0,
                        }}
                    >
                        {isExpanded ? '-' : '+'}
                    </button>
                ) : (
                    <span style={{ width: '18px', display: 'inline-block' }} />
                )}
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#1f1f1f', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{description}</div>
                <label style={{ display: 'inline-flex', justifySelf: 'center', justifyContent: 'center', alignItems: 'center', minWidth: '88px' }}>
                    <input
                        type="checkbox"
                        checked={allowAccess}
                        onChange={(e) => onAllowAccessChange(node.node.securityDetailID, e.target.checked)}
                    />
                </label>
                <label style={{ display: 'inline-flex', justifySelf: 'center', justifyContent: 'center', alignItems: 'center', minWidth: '90px' }}>
                    <input
                        type="checkbox"
                        checked={allowEdit}
                        onChange={(e) => onAllowEditChange(node.node.securityDetailID, e.target.checked)}
                    />
                </label>
            </div>
            {hasChildren && isExpanded && (
                <div style={{ marginLeft: 12 }}>
                    {node.children.map(child => renderSecurityDetailNode(child, depth + 1, expanded, onToggle, allowAccessMap, allowEditMap, onAllowAccessChange, onAllowEditChange))}
                </div>
            )}
        </div>
    );
};

const UserRoles: React.FC<UserRolesProps> = (props) => {
    // When navigating away to another menu item and back, the parent App restores
    // the full previous state (including filters/sort/dialogs) via stateToLoad/onSaveState,
    // so the page doesn't need to refetch/refilter/reselect. On first visit there is no
    // stateToLoad, so fall back to the defaults below.
    const [state, setState] = useState<UserRolesState>(props.stateToLoad ?? {
        rolesData: [],
        menusData: [],
        usersByRoleData: [],
        loadingRoles: false,
        loadingMenus: false,
        loadingUsersByRole: false,
        errorMsg: '',
        sort: [{ field: 'display', dir: 'asc' }],
        filterTexts: {},
        headerFocusField: null,
        selectedRoleKey: null,
        editRoleKey: null,
        originalItem: null,
        deleteConfirmItem: null,
        menuDataItem: null,
        menuAnchor: null,
        menuPosition: null,
        copyToProjectDialogOpen: false,
        copyToProjectRole: null,
        copyToProjectTarget: null,
        copyToProjectName: '',
        copyToProjectNameDialogOpen: false,
        copyToProjectDuplicateRole: null,
        copyToProjectConfirmOpen: false,
        copyToProjectError: '',
        newDialogOpen: false,
        newDialogDisplay: '',
        newDialogError: '',
        checkedMenuIds: new Set<number>(),
        collapsedMenuIds: new Set<number>(),
        usersByRoleSort: [{ field: 'display', dir: 'asc' }],
        usersByRoleFilterTexts: {},
        usersByRoleHeaderFocusField: null,
        selectedUserByRoleKey: null,
        usersDeleteConfirmItem: null,
        usersAddDialogOpen: false,
        usersAddDialogLoading: false,
        usersAddDialogSaving: false,
        usersAddDialogFilterText: '',
        usersAddDialogData: [],
        usersAddDialogSelected: new Set<string>(),
        usersAddDialogError: '',
        securityDetailDialogOpen: false,
        securityDetailDialogMode: null,
        securityDetailDialogTitle: '',
        securityDetailDialogLoading: false,
        securityDetailDialogError: '',
        securityDetailDialogStatus: null,
        securityDetailCopying: false,
        securityDetailData: [],
        securityDetailAllowAccessMap: {},
        securityDetailAllowEditMap: {},
        securityDetailListLoading: false,
        securityDetailListData: [],
        securityDetailListSelected: new Set<string>(),
        securityDetailListSelectedRow: null,
        securityDetailListFilterText: '',
        securityDetailSourceRoleDialogOpen: false,
        securityDetailSourceRoleFilterText: '',
        securityDetailSourceRoleSelected: null,
    });

    const stateRef = useRef(state);
    stateRef.current = state;

    const menuRef = useRef<HTMLDivElement | null>(null);
    const hierarchyContentRef = useRef<HTMLDivElement | null>(null);
    const hierarchyScrollTopRef = useRef(0);
    const shouldRestoreHierarchyScrollRef = useRef(false);
    const securitiesRequestSeqRef = useRef(0);
    const usersByRoleRequestSeqRef = useRef(0);

    const handleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, headerFocusField: field }));
    }, []);

    const handleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, headerFocusField: null }));
    }, []);

    const toggleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.sort && prev.sort.length > 0 ? prev.sort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, sort: newSort };
        });
    }, []);

    const handleHeaderSortClick = useCallback((field: string, e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('input, select, textarea, button')) return;
        toggleSort(field);
    }, [toggleSort]);

    const handleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({
            ...prev,
            filterTexts: {
                ...prev.filterTexts,
                [field]: e.target.value,
            },
        }));
    }, []);

    const fetchUserRoles = useCallback(() => {
        setState(prev => ({ ...prev, loadingRoles: true, errorMsg: '' }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetUserRolesForDropDown';

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK': {
                        const normalizedRows = Array.isArray(res.results)
                            ? res.results.map((item: any, index: number) => normalizeUserRole(item, index))
                            : [];
                        setState(prev => ({
                            ...prev,
                            rolesData: normalizedRows,
                            loadingRoles: false,
                            selectedRoleKey: prev.selectedRoleKey && normalizedRows.some(r => r.roleKey === prev.selectedRoleKey)
                                ? prev.selectedRoleKey
                                : (normalizedRows.length > 0 ? normalizedRows[0].roleKey : null),
                        }));
                        break;
                    }
                    case 'UNAUTH': {
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        setState(prev => ({ ...prev, loadingRoles: false }));
                        break;
                    }
                    default:
                        setState(prev => ({
                            ...prev,
                            errorMsg: 'Error fetching user roles: ' + (res.callStatusMessage || ''),
                            loadingRoles: false,
                        }));
                }
            })
            .catch(err => {
                setState(prev => ({ ...prev, errorMsg: 'Error fetching user roles: ' + err.toString(), loadingRoles: false }));
            });
    }, [props.userInf, props.updateUserInfo]);

    const fetchMenuHierarchy = useCallback(() => {
        if (hierarchyContentRef.current) {
            hierarchyScrollTopRef.current = hierarchyContentRef.current.scrollTop;
            shouldRestoreHierarchyScrollRef.current = true;
        }

        setState(prev => ({ ...prev, loadingMenus: true, errorMsg: prev.errorMsg }));
        const url = props.userInf.currProject.apiUrl + '/api/TMI/GetMenus';

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                switch (res.callStatus) {
                    case 'OK': {
                        const rows = Array.isArray(res.results)
                            ? res.results.map((item: any) => normalizeMenu(item))
                            : [];
                        setState(prev => ({ ...prev, menusData: rows, collapsedMenuIds: new Set<number>(), loadingMenus: false }));
                        break;
                    }
                    case 'UNAUTH': {
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        setState(prev => ({ ...prev, loadingMenus: false }));
                        break;
                    }
                    default:
                        setState(prev => ({
                            ...prev,
                            errorMsg: prev.errorMsg || ('Error fetching menus: ' + (res.callStatusMessage || '')),
                            loadingMenus: false,
                        }));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    errorMsg: prev.errorMsg || ('Error fetching menus: ' + err.toString()),
                    loadingMenus: false,
                }));
            });
    }, [props.userInf, props.updateUserInfo]);

    const fetchCheckedSecuritiesForRole = useCallback((userRoleID: number) => {
        const requestSeq = ++securitiesRequestSeqRef.current;
        const url = `${props.userInf.currProject.apiUrl}/api/TMI/GetSecuritiesByUserRoleForDropDown?userRoleID=${userRoleID}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (requestSeq !== securitiesRequestSeqRef.current) {
                    return;
                }

                switch (res.callStatus) {
                    case 'OK': {
                        const checkedIds = new Set<number>(
                            Array.isArray(res.results)
                                ? res.results
                                    .map((id: any) => Number(id))
                                    .filter((id: number) => Number.isFinite(id))
                                : []
                        );
                        setState(prev => ({ ...prev, checkedMenuIds: checkedIds }));
                        break;
                    }
                    case 'UNAUTH': {
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        break;
                    }
                    default:
                        setState(prev => ({
                            ...prev,
                            checkedMenuIds: new Set<number>(),
                            errorMsg: prev.errorMsg || ('Error fetching role securities: ' + (res.callStatusMessage || '')),
                        }));
                }
            })
            .catch(err => {
                if (requestSeq !== securitiesRequestSeqRef.current) {
                    return;
                }

                setState(prev => ({
                    ...prev,
                    checkedMenuIds: new Set<number>(),
                    errorMsg: prev.errorMsg || ('Error fetching role securities: ' + err.toString()),
                }));
            });
    }, [props.userInf, props.updateUserInfo]);

    const fetchUsersByRole = useCallback((userRoleID: number) => {
        const requestSeq = ++usersByRoleRequestSeqRef.current;
        setState(prev => ({ ...prev, loadingUsersByRole: true }));
        const url = `${props.userInf.currProject.apiUrl}/api/TMI/GetUsersByUserRole?userRoleID=${userRoleID}`;

        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (requestSeq !== usersByRoleRequestSeqRef.current) {
                    return;
                }

                switch (res.callStatus) {
                    case 'OK': {
                        const users = Array.isArray(res.results)
                            ? res.results.map((item: any, index: number) => ({
                                clientRowKey: `${String(item.value ?? item.Value ?? item.userID ?? item.UserID ?? '')}_${index}`,
                                value: String(item.value ?? item.Value ?? item.userID ?? item.UserID ?? ''),
                                display: String(item.display ?? item.Display ?? item.userName ?? item.UserName ?? ''),
                            }))
                            : [];
                        setState(prev => ({
                            ...prev,
                            usersByRoleData: users,
                            loadingUsersByRole: false,
                            selectedUserByRoleKey: prev.selectedUserByRoleKey && users.some((u: UsersByRoleRow) => u.clientRowKey === prev.selectedUserByRoleKey)
                                ? prev.selectedUserByRoleKey
                                : (users.length > 0 ? users[0].clientRowKey : null),
                        }));
                        break;
                    }
                    case 'UNAUTH': {
                        const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                        props.updateUserInfo(uInf);
                        setState(prev => ({ ...prev, loadingUsersByRole: false }));
                        break;
                    }
                    default:
                        setState(prev => ({
                            ...prev,
                            usersByRoleData: [],
                            loadingUsersByRole: false,
                            selectedUserByRoleKey: null,
                            errorMsg: prev.errorMsg || ('Error fetching users by role: ' + (res.callStatusMessage || '')),
                        }));
                }
            })
            .catch(err => {
                if (requestSeq !== usersByRoleRequestSeqRef.current) {
                    return;
                }

                setState(prev => ({
                    ...prev,
                    usersByRoleData: [],
                    loadingUsersByRole: false,
                    selectedUserByRoleKey: null,
                    errorMsg: prev.errorMsg || ('Error fetching users by role: ' + err.toString()),
                }));
            });
    }, [props.userInf, props.updateUserInfo]);

    const saveCheckedSecuritiesForRole = useCallback((userRoleID: number, checkedMenuIds: Set<number>) => {
        const url = `${props.userInf.currProject.apiUrl}/api/TMI/AddOrUpdateSecuritiesByUserRole`;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
            body: JSON.stringify({
                selected: userRoleID,
                checked: Array.from(checkedMenuIds).map(id => Number(id)).filter(id => Number.isFinite(id)),
            }),
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                } else if (res.callStatus !== 'OK') {
                    setState(prev => ({
                        ...prev,
                        errorMsg: 'Error saving role securities: ' + (res.callStatusMessage || ''),
                    }));
                } else {
                    window.dispatchEvent(new Event(MENU_STRUCTURE_CHANGED_EVENT));
                }
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    errorMsg: 'Error saving role securities: ' + err.toString(),
                }));
            });
    }, [props.userInf, props.updateUserInfo]);

    useEffect(() => {
        fetchUserRoles();
        fetchMenuHierarchy();
        return () => {
            if (props.onSaveState && props.menuItemId !== undefined) {
                props.onSaveState(props.menuItemId, stateRef.current);
            }
        };
    }, [fetchUserRoles, fetchMenuHierarchy]);

    useEffect(() => {
        const handleMenuStructureChanged = () => {
            fetchMenuHierarchy();
        };

        window.addEventListener(MENU_STRUCTURE_CHANGED_EVENT, handleMenuStructureChanged as EventListener);
        return () => {
            window.removeEventListener(MENU_STRUCTURE_CHANGED_EVENT, handleMenuStructureChanged as EventListener);
        };
    }, [fetchMenuHierarchy]);

    useEffect(() => {
        if (state.loadingMenus) {
            return;
        }

        if (!shouldRestoreHierarchyScrollRef.current) {
            return;
        }

        if (!hierarchyContentRef.current) {
            return;
        }

        requestAnimationFrame(() => {
            if (!hierarchyContentRef.current) return;
            hierarchyContentRef.current.scrollTop = hierarchyScrollTopRef.current;
            shouldRestoreHierarchyScrollRef.current = false;
        });
    }, [state.loadingMenus, state.menusData]);

    useEffect(() => {
        if (!state.selectedRoleKey) {
            setState(prev => ({
                ...prev,
                checkedMenuIds: new Set<number>(),
                usersByRoleData: [],
                loadingUsersByRole: false,
                selectedUserByRoleKey: null,
            }));
            return;
        }

        const selectedRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        if (!selectedRole) {
            setState(prev => ({
                ...prev,
                checkedMenuIds: new Set<number>(),
                usersByRoleData: [],
                loadingUsersByRole: false,
                selectedUserByRoleKey: null,
            }));
            return;
        }

        const userRoleID = Number(selectedRole.value);
        if (!Number.isInteger(userRoleID) || userRoleID < 0) {
            setState(prev => ({
                ...prev,
                checkedMenuIds: new Set<number>(),
                usersByRoleData: [],
                loadingUsersByRole: false,
                selectedUserByRoleKey: null,
            }));
            return;
        }

        fetchCheckedSecuritiesForRole(userRoleID);
        fetchUsersByRole(userRoleID);
    }, [state.selectedRoleKey, state.rolesData, fetchCheckedSecuritiesForRole, fetchUsersByRole]);

    const filteredRoles = useMemo(() => {
        const filterTexts = state.filterTexts || {};
        if (Object.keys(filterTexts).length === 0 || Object.values(filterTexts).every(v => !v)) {
            return state.rolesData;
        }

        return state.rolesData.filter(item => {
            return Object.entries(filterTexts).every(([field, query]) => {
                if (!query) return true;
                const value = String((item as any)[field] ?? '').toLowerCase();
                return value.includes(query.trim().toLowerCase());
            });
        });
    }, [state.rolesData, state.filterTexts]);

    const rolesView = useMemo(() => orderBy(filteredRoles, state.sort), [filteredRoles, state.sort]);
    const filteredUsersByRole = useMemo(() => {
        const filterTexts = state.usersByRoleFilterTexts || {};
        if (Object.keys(filterTexts).length === 0 || Object.values(filterTexts).every(v => !v)) {
            return state.usersByRoleData;
        }

        return state.usersByRoleData.filter(item => {
            return Object.entries(filterTexts).every(([field, query]) => {
                if (!query) return true;
                const value = String((item as any)[field] ?? '').toLowerCase();
                return value.includes(query.trim().toLowerCase());
            });
        });
    }, [state.usersByRoleData, state.usersByRoleFilterTexts]);
    const usersByRoleView = useMemo(() => orderBy(filteredUsersByRole, state.usersByRoleSort), [filteredUsersByRole, state.usersByRoleSort]);
    const menuTree = useMemo(() => buildMenuTree(state.menusData), [state.menusData]);
    const visibleMenuTree = useMemo(() => menuTree.filter(node => node.node.securityID === 1), [menuTree]);
    const visibleSecurityIds = useMemo(() => collectSecurityIds(visibleMenuTree), [visibleMenuTree]);

    const onRefreshClick = useCallback(() => {
        fetchUserRoles();
        fetchMenuHierarchy();
    }, [fetchUserRoles, fetchMenuHierarchy]);

    const handleRowClick = useCallback((roleKey: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setState(prev => ({ ...prev, selectedRoleKey: roleKey }));
    }, []);

    const handleEnterEdit = useCallback((item: UserRoleRow) => {
        setState(prev => ({ ...prev, editRoleKey: item.roleKey, originalItem: { ...item } }));
    }, []);

    const handleCancel = useCallback(() => {
        setState(prev => ({
            ...prev,
            rolesData: prev.originalItem
                ? prev.rolesData.map(item => item.roleKey === prev.originalItem!.roleKey ? prev.originalItem! : item)
                : prev.rolesData,
            editRoleKey: null,
            originalItem: null,
        }));
    }, []);

    const handleSave = useCallback(() => {
        setState(prev => ({ ...prev, editRoleKey: null, originalItem: null }));
    }, []);

    const openDeleteConfirm = useCallback((item: UserRoleRow) => {
        setState(prev => ({ ...prev, deleteConfirmItem: item, menuDataItem: null, menuAnchor: null, menuPosition: null }));
    }, []);

    const openNewDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            newDialogOpen: true,
            newDialogDisplay: '',
            newDialogError: '',
        }));
    }, []);

    const closeNewDialog = useCallback(() => {
        setState(prev => ({ ...prev, newDialogOpen: false, newDialogError: '' }));
    }, []);

    const submitNewRole = useCallback(() => {
        setState(prev => {
            const display = prev.newDialogDisplay.trim();
            const value = '0';

            if (!display) {
                return { ...prev, newDialogError: 'Role is required.' };
            }

            const nextRow: UserRoleRow = {
                roleKey: `new_${value}_${Date.now()}`,
                value,
                display,
            };

            const nextRoles = [...prev.rolesData, nextRow];
            return {
                ...prev,
                rolesData: nextRoles,
                selectedRoleKey: nextRow.roleKey,
                newDialogOpen: false,
                newDialogDisplay: '',
                newDialogError: '',
            };
        });
    }, []);

    const closeDeleteConfirm = useCallback(() => {
        setState(prev => ({ ...prev, deleteConfirmItem: null }));
    }, []);

    const confirmDelete = useCallback(() => {
        setState(prev => {
            const deleting = prev.deleteConfirmItem;
            if (!deleting) return prev;
            const next = prev.rolesData.filter(item => item.roleKey !== deleting.roleKey);
            const nextSelected = prev.selectedRoleKey === deleting.roleKey
                ? (next.length > 0 ? next[0].roleKey : null)
                : prev.selectedRoleKey;
            return {
                ...prev,
                rolesData: next,
                selectedRoleKey: nextSelected,
                editRoleKey: prev.editRoleKey === deleting.roleKey ? null : prev.editRoleKey,
                originalItem: prev.editRoleKey === deleting.roleKey ? null : prev.originalItem,
                deleteConfirmItem: null,
            };
        });
    }, []);

    const openRowMenu = useCallback((dataItem: UserRoleRow, anchor: HTMLElement) => {
        const rect = anchor.getBoundingClientRect();
        const scrollX = window.scrollX || window.pageXOffset || 0;
        const scrollY = window.scrollY || window.pageYOffset || 0;
        setState(prev => ({
            ...prev,
            selectedRoleKey: dataItem.roleKey,
            menuDataItem: dataItem,
            menuAnchor: anchor,
            menuPosition: {
                left: Math.round(rect.left + scrollX),
                top: Math.round(rect.bottom + scrollY),
            },
        }));
    }, []);

    const closeRowMenu = useCallback(() => {
        setState(prev => ({ ...prev, menuDataItem: null, menuAnchor: null, menuPosition: null }));
    }, []);

    const closeSecurityDetailDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            securityDetailDialogOpen: false,
            securityDetailDialogMode: null,
            securityDetailDialogTitle: '',
            securityDetailDialogLoading: false,
            securityDetailDialogError: '',
            securityDetailDialogStatus: null,
            securityDetailCopying: false,
            securityDetailData: [],
            securityDetailAllowAccessMap: {},
            securityDetailAllowEditMap: {},
            securityDetailListLoading: false,
            securityDetailListData: [],
            securityDetailListSelected: new Set<string>(),
            securityDetailListSelectedRow: null,
            securityDetailListFilterText: '',
        }));
    }, []);

    const updateSecurityDetailAllowAccess = useCallback(async (securityDetailID: string, value: boolean) => {
        if (!state.selectedRoleKey || !state.securityDetailListSelectedRow || !state.securityDetailDialogMode) {
            return;
        }

        const selectedRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        const userRoleId = Number(selectedRole?.value);
        const typeId = Number(state.securityDetailListSelectedRow);
        if (!Number.isFinite(userRoleId) || !Number.isFinite(typeId)) {
            return;
        }

        const endpoint = state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType
            ? (value
                ? `${props.userInf.currProject.apiUrl}/api/TMI/AddUserRoleItemTypeSecurityDetail?userRoleID=${userRoleId}&itemTypeID=${typeId}&securityDetailID=${encodeURIComponent(securityDetailID)}`
                : `${props.userInf.currProject.apiUrl}/api/TMI/DeleteUserRoleItemTypeSecurityDetail?userRoleID=${userRoleId}&packListTypeID=${typeId}&securityDetailID=${encodeURIComponent(securityDetailID)}`)
            : (value
                ? `${props.userInf.currProject.apiUrl}/api/TMI/AddUserRolePackListTypeSecurityDetail?userRoleID=${userRoleId}&packListTypeID=${typeId}&securityDetailID=${encodeURIComponent(securityDetailID)}`
                : `${props.userInf.currProject.apiUrl}/api/TMI/DeleteUserRolePackListTypeSecurityDetail?userRoleID=${userRoleId}&packListTypeID=${typeId}&securityDetailID=${encodeURIComponent(securityDetailID)}`);

        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            });

            const json = await response.json();
            if (!response.ok || json.callStatus !== 'OK') {
                return;
            }

            setState(prev => ({
                ...prev,
                securityDetailAllowAccessMap: {
                    ...prev.securityDetailAllowAccessMap,
                    [securityDetailID]: value,
                },
            }));
        } catch {
            // Intentionally ignored for now; API failure will leave the value unchanged.
        }
    }, [props.userInf, state.rolesData, state.securityDetailDialogMode, state.securityDetailListSelectedRow, state.selectedRoleKey]);

    const updateSecurityDetailAllowEdit = useCallback(async (securityDetailID: string, value: boolean) => {
        if (!state.selectedRoleKey || !state.securityDetailListSelectedRow || !state.securityDetailDialogMode) {
            return;
        }

        const selectedRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        const userRoleId = Number(selectedRole?.value);
        const typeId = Number(state.securityDetailListSelectedRow);
        if (!Number.isFinite(userRoleId) || !Number.isFinite(typeId)) {
            return;
        }

        const shouldEnsureAccess = !state.securityDetailAllowAccessMap[securityDetailID] && value;
        if (shouldEnsureAccess) {
            setState(prev => ({
                ...prev,
                securityDetailAllowAccessMap: {
                    ...prev.securityDetailAllowAccessMap,
                    [securityDetailID]: true,
                },
            }));
        }

        const endpoint = state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType
            ? `${props.userInf.currProject.apiUrl}/api/TMI/UpdateUserRoleItemTypeSecurityDetail?userRoleID=${userRoleId}&itemTypeID=${typeId}&securityDetailID=${encodeURIComponent(securityDetailID)}&allowEdit=${value}`
            : `${props.userInf.currProject.apiUrl}/api/TMI/UpdateUserRolePackListTypeSecurityDetail?userRoleID=${userRoleId}&packListTypeID=${typeId}&securityDetailID=${encodeURIComponent(securityDetailID)}&allowUpdate=${value}`;

        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            });

            const json = await response.json();
            if (!response.ok || json.callStatus !== 'OK') {
                return;
            }

            setState(prev => ({
                ...prev,
                securityDetailAllowEditMap: {
                    ...prev.securityDetailAllowEditMap,
                    [securityDetailID]: value,
                },
                securityDetailAllowAccessMap: {
                    ...prev.securityDetailAllowAccessMap,
                    [securityDetailID]: prev.securityDetailAllowAccessMap[securityDetailID] || value,
                },
            }));
        } catch {
            // Intentionally ignored for now; API failure will leave the UI value unchanged.
        }
    }, [props.userInf, state.securityDetailAllowAccessMap, state.securityDetailDialogMode, state.securityDetailListSelectedRow, state.selectedRoleKey]);

    const selectSecurityDetailListRow = useCallback((value: string) => {
        setState(prev => ({
            ...prev,
            securityDetailListSelectedRow: value,
        }));
    }, []);

    const fetchSecurityDetailListData = useCallback(async (mode: SecurityDetailDialogMode) => {
        const endpoint = mode === SecurityDetailDialogMode.ItemType
            ? `${props.userInf.currProject.apiUrl}/api/TMI/GetItemTypesForDropDown`
            : `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistTypesForDropDown`;

        setState(prev => ({
            ...prev,
            securityDetailListLoading: true,
            securityDetailListData: [],
            securityDetailListSelected: new Set<string>(),
            securityDetailListSelectedRow: null,
            securityDetailListFilterText: '',
        }));

        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            });

            const json = await response.json();
            if (!response.ok || json.callStatus !== 'OK' || !Array.isArray(json.results)) {
                throw new Error(json.callStatusMessage || 'Failed to load list data.');
            }

            const data: LookupListItem[] = json.results
                .map((item: any) => ({
                    value: String(item.value ?? item.Value ?? item.id ?? item.ID ?? item.itemTypeID ?? item.ItemTypeID ?? item.packlistTypeID ?? item.PacklistTypeID ?? item.packListTypeID ?? item.PackListTypeID ?? ''),
                    display: String(item.display ?? item.Display ?? item.description ?? item.Description ?? item.name ?? item.Name ?? ''),
                }))
                .filter((entry: LookupListItem) => entry.value !== '' && entry.display !== '');

            setState(prev => ({
                ...prev,
                securityDetailListLoading: false,
                securityDetailListData: data,
                securityDetailListSelected: new Set<string>(),
                securityDetailListSelectedRow: null,
            }));
        } catch (error: any) {
            setState(prev => ({
                ...prev,
                securityDetailListLoading: false,
                securityDetailListData: [],
                securityDetailListSelected: new Set<string>(),
                securityDetailDialogError: error?.message || 'An error occurred while loading the list.',
            }));
        }
    }, [props.userInf]);

    const toggleSecurityDetailListSelection = useCallback((value: string) => {
        setState(prev => {
            if (prev.securityDetailListSelectedRow === value) {
                const next = new Set(prev.securityDetailListSelected);
                next.delete(value);
                return { ...prev, securityDetailListSelected: next };
            }

            const next = new Set(prev.securityDetailListSelected);
            if (next.has(value)) {
                next.delete(value);
            } else {
                next.add(value);
            }
            return { ...prev, securityDetailListSelected: next };
        });
    }, []);

    const copySecurityDetailSelectedToChecked = useCallback(async () => {
        if (!state.selectedRoleKey || !state.securityDetailListSelectedRow || !state.securityDetailDialogMode) {
            return;
        }

        setState(prev => ({ ...prev, securityDetailDialogStatus: null }));

        const selectedRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        const userRoleId = Number(selectedRole?.value);
        const selectedTypeId = Number(state.securityDetailListSelectedRow);
        const checkedTypeIds = Array.from(state.securityDetailListSelected)
            .map(value => Number(value))
            .filter(value => Number.isFinite(value) && value >= 0);

        if (!Number.isFinite(userRoleId) || userRoleId < 0) {
            return;
        }

        if (!Number.isFinite(selectedTypeId) || selectedTypeId < 0) {
            return;
        }

        if (checkedTypeIds.length === 0 || checkedTypeIds.includes(selectedTypeId)) {
            setState(prev => ({
                ...prev,
                securityDetailDialogError: 'Select at least one checked type and ensure the selected type is not one of those checked values.',
                securityDetailDialogStatus: { type: 'error', message: 'Copy was not started. Check the selected and checked types.' },
            }));
            return;
        }

        const endpoint = state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType
            ? `${props.userInf.currProject.apiUrl}/api/TMI/CopyUserRoleItemTypeSecurityDetailSelectedToChecked`
            : `${props.userInf.currProject.apiUrl}/api/TMI/CopyUserRolePackListTypeSecurityDetailSelectedToChecked`;

        setState(prev => ({ ...prev, securityDetailCopying: true }));
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
                body: JSON.stringify({
                    ID: userRoleId,
                    CheckedSelected: {
                        Checked: checkedTypeIds,
                        Selected: selectedTypeId,
                    },
                }),
            });

            const json = await response.json();
            if (json.callStatus === 'UNAUTH') {
                const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                props.updateUserInfo(uInf);
                setState(prev => ({
                    ...prev,
                    securityDetailDialogStatus: { type: 'error', message: 'Copy failed: authorization is no longer valid.' },
                }));
                return;
            }

            if (!response.ok || json.callStatus !== 'OK') {
                setState(prev => ({
                    ...prev,
                    securityDetailDialogError: json.callStatusMessage || 'Unable to copy the selected security details to the checked types.',
                    securityDetailDialogStatus: { type: 'error', message: 'Copy failed.' },
                }));
                return;
            }

            setState(prev => ({
                ...prev,
                securityDetailDialogError: '',
                securityDetailDialogStatus: { type: 'success', message: 'Security details copied successfully.' },
            }));
        } catch (error: any) {
            setState(prev => ({
                ...prev,
                securityDetailDialogError: error?.message || 'An error occurred while copying security details.',
                securityDetailDialogStatus: { type: 'error', message: 'Copy failed.' },
            }));
        } finally {
            setState(prev => ({ ...prev, securityDetailCopying: false }));
        }
    }, [props.userInf, props.updateUserInfo, state.rolesData, state.securityDetailDialogMode, state.securityDetailListSelected, state.securityDetailListSelectedRow, state.selectedRoleKey]);

    const loadSecurityDetailAllowEditForSelectedType = useCallback(async (selectedValue: string | null) => {
        if (!selectedValue || !state.selectedRoleKey) {
            setState(prev => ({ ...prev, securityDetailAllowAccessMap: {}, securityDetailAllowEditMap: {} }));
            return;
        }

        const selectedRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        const userRoleId = Number(selectedRole?.value);
        const selectedTypeId = Number(selectedValue);
        if (!Number.isFinite(userRoleId) || !Number.isFinite(selectedTypeId)) {
            setState(prev => ({ ...prev, securityDetailAllowAccessMap: {}, securityDetailAllowEditMap: {} }));
            return;
        }

        const endpoint = state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType
            ? `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRoleItemTypeSecurityDetails?userRoleID=${userRoleId}&itemTypeID=${selectedTypeId}`
            : `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRolePackListTypeSecurityDetails?userRoleID=${userRoleId}&packListTypeID=${selectedTypeId}`;

        try {
            const response = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            });

            const json = await response.json();
            if (!response.ok || json.callStatus !== 'OK' || !Array.isArray(json.results)) {
                return;
            }

            const nextAccessMap: { [key: string]: boolean } = {};
            const nextEditMap: { [key: string]: boolean } = {};
            json.results.forEach((item: any) => {
                const securityDetailId = String(item.securityDetailID ?? item.SecurityDetailID ?? '');
                if (!securityDetailId) {
                    return;
                }
                nextAccessMap[securityDetailId] = true;
                nextEditMap[securityDetailId] = Boolean(item.allowEdit ?? item.AllowEdit ?? false);
            });

            setState(prev => ({
                ...prev,
                securityDetailAllowAccessMap: nextAccessMap,
                securityDetailAllowEditMap: nextEditMap,
            }));
        } catch {
            setState(prev => ({ ...prev, securityDetailAllowAccessMap: {}, securityDetailAllowEditMap: {} }));
        }
    }, [props.userInf, state.rolesData, state.securityDetailDialogMode, state.selectedRoleKey]);

    const copySecurityDetailFromRoleToChecked = useCallback(async () => {
        if (!state.selectedRoleKey || !state.securityDetailDialogMode || !state.securityDetailSourceRoleSelected) {
            return;
        }

        setState(prev => ({ ...prev, securityDetailDialogStatus: null }));

        const targetRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        const sourceRole = state.rolesData.find(r => r.roleKey === state.securityDetailSourceRoleSelected);
        const targetUserRoleId = Number(targetRole?.value);
        const sourceUserRoleId = Number(sourceRole?.value);
        const checkedTypeIds = Array.from(state.securityDetailListSelected)
            .map(value => Number(value))
            .filter(value => Number.isFinite(value) && value >= 0);

        if (!Number.isFinite(targetUserRoleId) || targetUserRoleId < 0) {
            return;
        }

        if (!Number.isFinite(sourceUserRoleId) || sourceUserRoleId < 0) {
            return;
        }

        if (checkedTypeIds.length === 0) {
            setState(prev => ({
                ...prev,
                securityDetailDialogError: 'Select at least one checked type before copying from another user role.',
                securityDetailDialogStatus: { type: 'error', message: 'Copy was not started. Check at least one type.' },
            }));
            return;
        }

        const endpoint = state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType
            ? `${props.userInf.currProject.apiUrl}/api/TMI/CopyUserRoleItemTypeSecurityDetailUserRoleToChecked`
            : `${props.userInf.currProject.apiUrl}/api/TMI/CopyUserRolePackListTypeSecurityDetailUserRoleToChecked`;

        setState(prev => ({ ...prev, securityDetailCopying: true }));
        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
                body: JSON.stringify({
                    ID: targetUserRoleId,
                    CheckedSelected: {
                        Checked: checkedTypeIds,
                        Selected: sourceUserRoleId,
                    },
                }),
            });

            const json = await response.json();
            if (json.callStatus === 'UNAUTH') {
                const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                props.updateUserInfo(uInf);
                setState(prev => ({
                    ...prev,
                    securityDetailDialogStatus: { type: 'error', message: 'Copy failed: authorization is no longer valid.' },
                }));
                return;
            }

            if (!response.ok || json.callStatus !== 'OK') {
                setState(prev => ({
                    ...prev,
                    securityDetailDialogError: json.callStatusMessage || 'Unable to copy security details from the selected user role.',
                    securityDetailDialogStatus: { type: 'error', message: 'Copy failed.' },
                }));
                return;
            }

            setState(prev => ({
                ...prev,
                securityDetailDialogError: '',
                securityDetailDialogStatus: { type: 'success', message: 'Security details copied successfully.' },
                securityDetailSourceRoleDialogOpen: false,
                securityDetailSourceRoleFilterText: '',
                securityDetailSourceRoleSelected: null,
            }));

            if (state.securityDetailListSelectedRow) {
                await loadSecurityDetailAllowEditForSelectedType(state.securityDetailListSelectedRow);
            }
        } catch (error: any) {
            setState(prev => ({
                ...prev,
                securityDetailDialogError: error?.message || 'An error occurred while copying from another user role.',
                securityDetailDialogStatus: { type: 'error', message: 'Copy failed.' },
            }));
        } finally {
            setState(prev => ({ ...prev, securityDetailCopying: false }));
        }
    }, [loadSecurityDetailAllowEditForSelectedType, props.userInf, props.updateUserInfo, state.rolesData, state.securityDetailDialogMode, state.securityDetailListSelected, state.securityDetailListSelectedRow, state.securityDetailSourceRoleSelected, state.selectedRoleKey]);

    const openSecurityDetailDialog = useCallback(async (mode: SecurityDetailDialogMode) => {
        closeRowMenu();

        const selectedRole =
            state.rolesData.find(r => r.roleKey === state.selectedRoleKey);

        const roleName = selectedRole?.display ?? '';
        
const title = mode === SecurityDetailDialogMode.ItemType
    ? `Item Type Security Details - ${roleName}`
    : `PackList Type Security Details - ${roleName}`;

        setState(prev => ({
            ...prev,
            securityDetailDialogOpen: true,
            securityDetailDialogMode: mode,
            securityDetailDialogTitle: title,
            securityDetailDialogLoading: true,
            securityDetailDialogError: '',
            securityDetailDialogStatus: null,
            securityDetailData: [],
            securityDetailAllowAccessMap: {},
            securityDetailAllowEditMap: {},
            securityDetailListLoading: true,
            securityDetailListData: [],
            securityDetailListSelected: new Set<string>(),
            securityDetailListSelectedRow: null,
            securityDetailListFilterText: '',
        }));

        try {
            const [hierarchyResponse, listResponse] = await Promise.all([
                fetch(props.userInf.currProject.apiUrl + '/api/TMI/GetSecurityDetailsHierarchy', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                }),
                fetch(mode === SecurityDetailDialogMode.ItemType
                    ? `${props.userInf.currProject.apiUrl}/api/TMI/GetItemTypesForDropDown`
                    : `${props.userInf.currProject.apiUrl}/api/TMI/GetPacklistTypesForDropDown`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': 'Bearer ' + props.userInf.token,
                    },
                })
            ]);

            const hierarchyJson = await hierarchyResponse.json();
            if (!hierarchyResponse.ok || hierarchyJson.callStatus !== 'OK' || !Array.isArray(hierarchyJson.results)) {
                throw new Error(hierarchyJson.callStatusMessage || 'Failed to load security details hierarchy.');
            }

            const hierarchyRows: SecurityDetailRow[] = hierarchyJson.results.map((item: any) => ({
                securityDetailID: String(item.securityDetailID ?? item.SecurityDetailID ?? ''),
                parentSecurityDetailID: item.parentSecurityDetailID ?? item.ParentSecurityDetailID ?? null,
                securityLabel: String(item.securityLabel ?? item.SecurityLabel ?? '').trim(),
                securityDescription: String(item.securityDescription ?? item.SecurityDescription ?? '').trim(),
            }));

            const filteredRows = filterSecurityDetailRowsByMode(hierarchyRows, mode)
                .map(row => ({ ...row, allowEdit: false }));

            const listJson = await listResponse.json();
            if (!listResponse.ok || listJson.callStatus !== 'OK' || !Array.isArray(listJson.results)) {
                throw new Error(listJson.callStatusMessage || 'Failed to load the list data.');
            }

            const listData: LookupListItem[] = listJson.results
                .map((item: any) => ({
                    value: String(item.value ?? item.Value ?? item.id ?? item.ID ?? item.itemTypeID ?? item.ItemTypeID ?? item.packlistTypeID ?? item.PacklistTypeID ?? item.packListTypeID ?? item.PackListTypeID ?? ''),
                    display: String(item.display ?? item.Display ?? item.description ?? item.Description ?? item.name ?? item.Name ?? ''),
                }))
                .filter((entry: LookupListItem) => entry.value !== '' && entry.display !== '');

            setState(prev => ({
                ...prev,
                securityDetailDialogLoading: false,
                securityDetailDialogError: '',
                securityDetailData: filteredRows,
                securityDetailAllowAccessMap: {},
                securityDetailAllowEditMap: {},
                securityDetailListLoading: false,
                securityDetailListData: listData,
                securityDetailListSelected: new Set<string>(),
                securityDetailListSelectedRow: null,
            }));
        } catch (error: any) {
            setState(prev => ({
                ...prev,
                securityDetailDialogLoading: false,
                securityDetailListLoading: false,
                securityDetailDialogError: error?.message || 'An error occurred while loading the hierarchy.',
            }));
        }
    }, [closeRowMenu, props.userInf, state.rolesData, state.selectedRoleKey]);

    const openCopyToProjectDialog = useCallback((role: UserRoleRow) => {
        setState(prev => ({
            ...prev,
            copyToProjectDialogOpen: true,
            copyToProjectRole: role,
            menuDataItem: null,
            menuAnchor: null,
            menuPosition: null,
        }));
    }, []);

    const closeCopyToProjectDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            copyToProjectDialogOpen: false,
            copyToProjectRole: null,
        }));
    }, []);

    const handleCopyToProjectSelection = useCallback((project: LookupListItem) => {
        const selectedRole = state.copyToProjectRole ?? state.rolesData.find(r => r.roleKey === state.selectedRoleKey) ?? null;
        closeCopyToProjectDialog();

        if (!selectedRole) {
            return;
        }

        setState(prev => ({
            ...prev,
            copyToProjectTarget: project,
            copyToProjectName: selectedRole.display,
            copyToProjectNameDialogOpen: true,
            copyToProjectError: '',
            copyToProjectRole: null,
        }));
    }, [closeCopyToProjectDialog, state.copyToProjectRole, state.rolesData, state.selectedRoleKey]);

    const closeCopyToProjectNameDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            copyToProjectNameDialogOpen: false,
            copyToProjectTarget: null,
            copyToProjectName: '',
            copyToProjectError: '',
            copyToProjectDuplicateRole: null,
            copyToProjectConfirmOpen: false,
        }));
    }, []);

    const reopenCopyToProjectNameDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            copyToProjectConfirmOpen: false,
            copyToProjectDuplicateRole: null,
            copyToProjectError: '',
            copyToProjectNameDialogOpen: true,
        }));
    }, []);

    const submitCopyToProjectRole = useCallback(async () => {
        const trimmedName = state.copyToProjectName.trim();
        if (!trimmedName) {
            setState(prev => ({ ...prev, copyToProjectError: 'New user role name is required.' }));
            return;
        }

        const targetProject = state.copyToProjectTarget;
        if (!targetProject) {
            setState(prev => ({ ...prev, copyToProjectError: 'No target project was selected.' }));
            return;
        }

        const projectID = Number(targetProject.value);
        if (!Number.isInteger(projectID) || projectID < 0) {
            setState(prev => ({ ...prev, copyToProjectError: 'The selected target project is invalid.' }));
            return;
        }

        const sourceRole = state.copyToProjectRole ?? state.rolesData.find(r => r.roleKey === state.selectedRoleKey) ?? null;
        const sourceUserRoleID = sourceRole ? Number(sourceRole.value) : NaN;
        if (!sourceRole || !Number.isInteger(sourceUserRoleID) || sourceUserRoleID < 0) {
            setState(prev => ({ ...prev, copyToProjectError: 'Unable to determine the source user role.' }));
            return;
        }

        setState(prev => ({ ...prev, copyToProjectError: '' }));

        try {
            const url = `${props.userInf.currProject.apiUrl}/api/TMI/GetUserRolesFromProjectForDropDown?projectID=${projectID}`;
            const resp = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${props.userInf.token}`,
                },
            });
            const res = await resp.json();

            if (res.callStatus === 'UNAUTH') {
                const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                props.updateUserInfo(uInf);
                return;
            }

            if (res.callStatus !== 'OK') {
                setState(prev => ({
                    ...prev,
                    copyToProjectError: 'Unable to verify role names in the selected project: ' + (res.callStatusMessage || 'Unknown error.'),
                }));
                return;
            }

            const existingRoles: any[] = Array.isArray(res.results) ? res.results : [];
            const duplicateExists = existingRoles.some(item => {
                const existingName = String(item.display ?? item.Display ?? item.name ?? item.Name ?? '').trim().toLowerCase();
                return existingName === trimmedName.toLowerCase();
            });

            if (duplicateExists) {
                const existingRole = existingRoles.find(item => {
                    const existingName = String(item.display ?? item.Display ?? item.name ?? item.Name ?? '').trim().toLowerCase();
                    return existingName === trimmedName.toLowerCase();
                });

                setState(prev => ({
                    ...prev,
                    copyToProjectDuplicateRole: existingRole
                        ? {
                            value: String(existingRole.value ?? existingRole.Value ?? existingRole.userRoleID ?? existingRole.UserRoleID ?? ''),
                            display: String(existingRole.display ?? existingRole.Display ?? existingRole.name ?? existingRole.Name ?? trimmedName),
                        }
                        : { value: '', display: trimmedName },
                    copyToProjectConfirmOpen: true,
                    copyToProjectNameDialogOpen: false,
                    copyToProjectError: `A user role named "${trimmedName}" already exists in ${targetProject.display}.`,
                }));
                return;
            }

            const copyUrl = `${props.userInf.currProject.apiUrl}/api/TMI/CopyUserRoleToProject?projectID=${projectID}&sourceUserRoleID=${sourceUserRoleID}&newUserRoleName=${encodeURIComponent(trimmedName)}`;
            const copyResp = await fetch(copyUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${props.userInf.token}`,
                },
            });
            const copyRes = await copyResp.json();

            if (copyRes.callStatus === 'UNAUTH') {
                const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                props.updateUserInfo(uInf);
                return;
            }

            if (copyRes.callStatus !== 'OK') {
                setState(prev => ({
                    ...prev,
                    copyToProjectError: 'Error copying user role: ' + (copyRes.callStatusMessage || 'Unknown error.'),
                }));
                return;
            }

            closeCopyToProjectNameDialog();
            setState(prev => ({
                ...prev,
                copyToProjectConfirmOpen: false,
                copyToProjectDuplicateRole: null,
            }));
        } catch (err) {
            setState(prev => ({
                ...prev,
                copyToProjectError: 'Error checking target project user roles: ' + (err as Error).toString(),
            }));
        }
    }, [closeCopyToProjectNameDialog, props.userInf, props.updateUserInfo, state.copyToProjectName, state.copyToProjectRole, state.copyToProjectTarget, state.rolesData, state.selectedRoleKey]);

    const setUserRoleReadOnly = useCallback(() => {
        const roleId = state.menuDataItem?.value;
        if (!roleId) {
            setState(prev => ({ ...prev, errorMsg: 'Unable to set user role as read only: invalid role ID.' }));
            return;
        }

        const userRoleID = Number(roleId);
        if (!Number.isFinite(userRoleID) || userRoleID < 0) {
            setState(prev => ({ ...prev, errorMsg: 'Unable to set user role as read only: invalid role ID.' }));
            return;
        }

        const url = `${props.userInf.currProject.apiUrl}/api/TMI/SetUserRoleReadOnly?userRoleID=${userRoleID}`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    closeRowMenu();
                    return;
                }

                if (res.callStatus !== 'OK') {
                    setState(prev => ({
                        ...prev,
                        errorMsg: 'Error setting role as read only: ' + (res.callStatusMessage || ''),
                    }));
                    closeRowMenu();
                    return;
                }

                closeRowMenu();
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    errorMsg: 'Error setting role as read only: ' + err.toString(),
                }));
                closeRowMenu();
            });
    }, [state.menuDataItem, props.userInf, props.updateUserInfo, closeRowMenu]);

    useEffect(() => {
        if (!state.menuDataItem) return;
        const handleDocumentClick = (e: MouseEvent) => {
            const target = e.target as Node;
            if (menuRef.current && !menuRef.current.contains(target) && state.menuAnchor && !state.menuAnchor.contains(target)) {
                closeRowMenu();
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, [state.menuDataItem, state.menuAnchor, closeRowMenu]);

    const buildHeaderCellRenderer = useCallback((field: string, title: string) => {
        return () => {
            const val = state.filterTexts?.[field] ?? '';
            const focused = state.headerFocusField === field;
            const sortDir = (state.sort && state.sort.length > 0 && state.sort[0].field === field)
                ? (state.sort[0].dir as string)
                : undefined;
            return (
                <th
                    className={`tmi-header-filter ${sortDir === 'asc' ? 'sort-asc' : sortDir === 'desc' ? 'sort-desc' : 'sort-none'}`}
                    onClick={(e) => handleHeaderSortClick(field, e)}
                >
                    <div className="k-link" onClick={() => toggleSort(field)} aria-hidden="true" />
                    <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                        <label
                            onClick={() => toggleSort(field)}
                            style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            <span>{title}</span>
                            <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </label>
                        <input
                            type="text"
                            className="k-input k-input-sm"
                            value={val}
                            onChange={(e) => handleHeaderFilterChange(field, e)}
                            onFocus={() => handleHeaderFocus(field)}
                            onBlur={() => handleHeaderBlur()}
                            onMouseDown={(e) => { e.stopPropagation(); }}
                            onClick={(e) => { e.stopPropagation(); }}
                        />
                    </div>
                    <div className="tmi-header-sort-zone" onClick={() => toggleSort(field)} />
                </th>
            );
        };
    }, [state.filterTexts, state.headerFocusField, state.sort, toggleSort, handleHeaderFilterChange, handleHeaderFocus, handleHeaderBlur, handleHeaderSortClick]);

    const toggleUsersByRoleSort = useCallback((field: string) => {
        setState(prev => {
            const current = prev.usersByRoleSort && prev.usersByRoleSort.length > 0 ? prev.usersByRoleSort[0] : undefined;
            let newSort: SortDescriptor[] = [];
            if (!current || current.field !== field) {
                newSort = [{ field, dir: 'asc' }];
            } else if (current.dir === 'asc') {
                newSort = [{ field, dir: 'desc' }];
            } else {
                newSort = [];
            }
            return { ...prev, usersByRoleSort: newSort };
        });
    }, []);

    const handleUsersByRoleHeaderSortClick = useCallback((field: string, e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        if (target.closest('input, select, textarea, button')) return;
        toggleUsersByRoleSort(field);
    }, [toggleUsersByRoleSort]);

    const handleUsersByRoleHeaderFocus = useCallback((field: string) => {
        setState(prev => ({ ...prev, usersByRoleHeaderFocusField: field }));
    }, []);

    const handleUsersByRoleHeaderBlur = useCallback(() => {
        setState(prev => ({ ...prev, usersByRoleHeaderFocusField: null }));
    }, []);

    const handleUsersByRoleHeaderFilterChange = useCallback((field: string, e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({
            ...prev,
            usersByRoleFilterTexts: {
                ...prev.usersByRoleFilterTexts,
                [field]: e.target.value,
            },
        }));
    }, []);

    const buildUsersByRoleHeaderCellRenderer = useCallback((field: string, title: string) => {
        return () => {
            const val = state.usersByRoleFilterTexts?.[field] ?? '';
            const focused = state.usersByRoleHeaderFocusField === field;
            const sortDir = (state.usersByRoleSort && state.usersByRoleSort.length > 0 && state.usersByRoleSort[0].field === field)
                ? (state.usersByRoleSort[0].dir as string)
                : undefined;
            return (
                <th
                    className={`tmi-header-filter ${sortDir === 'asc' ? 'sort-asc' : sortDir === 'desc' ? 'sort-desc' : 'sort-none'}`}
                    onClick={(e) => handleUsersByRoleHeaderSortClick(field, e)}
                >
                    <div className="k-link" onClick={() => toggleUsersByRoleSort(field)} aria-hidden="true" />
                    <div className={`tmi-floating-wrapper ${val ? 'has-value' : ''} ${focused ? 'focused' : ''}`}>
                        <label
                            onClick={() => toggleUsersByRoleSort(field)}
                            style={{ cursor: 'pointer', pointerEvents: 'auto', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            <span>{title}</span>
                            <span className="tmi-sort-glyph-inline">{sortDir === 'asc' ? '▲' : sortDir === 'desc' ? '▼' : '↕'}</span>
                        </label>
                        <input
                            type="text"
                            className="k-input k-input-sm"
                            value={val}
                            onChange={(e) => handleUsersByRoleHeaderFilterChange(field, e)}
                            onFocus={() => handleUsersByRoleHeaderFocus(field)}
                            onBlur={() => handleUsersByRoleHeaderBlur()}
                            onMouseDown={(e) => { e.stopPropagation(); }}
                            onClick={(e) => { e.stopPropagation(); }}
                        />
                    </div>
                    <div className="tmi-header-sort-zone" onClick={() => toggleUsersByRoleSort(field)} />
                </th>
            );
        };
    }, [state.usersByRoleFilterTexts, state.usersByRoleHeaderFocusField, state.usersByRoleSort, toggleUsersByRoleSort, handleUsersByRoleHeaderFilterChange, handleUsersByRoleHeaderFocus, handleUsersByRoleHeaderBlur, handleUsersByRoleHeaderSortClick]);

    const TextCell = useCallback((props: GridCellProps) => {
        const row = props.dataItem as UserRoleRow;
        const field = props.field as 'value' | 'display';
        const isSelected = state.selectedRoleKey === row.roleKey;
        const isEditing = state.editRoleKey === row.roleKey;
        const val = (row[field] ?? '') as string;
        const isIdField = field === 'value';

        if (isEditing && !isIdField) {
            return (
                <td className="tmi-dialog-grid-text-cell" onClick={(e) => handleRowClick(row.roleKey, e)} style={{ cursor: 'pointer', padding: '2px' }}>
                    <input
                        type="text"
                        className="k-input k-input-sm"
                        value={val}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onChange={(e) => {
                            const nextValue = e.target.value;
                            setState(prev => ({
                                ...prev,
                                rolesData: prev.rolesData.map(item => item.roleKey === row.roleKey ? { ...item, [field]: nextValue } : item),
                            }));
                        }}
                        style={{ width: '100%', border: '1px solid #999', borderRadius: '2px', boxSizing: 'border-box' }}
                    />
                </td>
            );
        }

        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleRowClick(row.roleKey, e)}
                style={{ cursor: 'pointer' }}
                title={String(val)}
            >
                {val}
            </td>
        );
    }, [state.selectedRoleKey, state.editRoleKey, handleRowClick]);

    const HamburgerHeaderCell = useCallback(() => {
        return (
            <th className="tmi-header-action-cell tmi-command-column">
                <button className="k-button k-button-sm k-rounded-md tmi-header-action-button" onClick={onRefreshClick} disabled={state.loadingRoles || state.loadingMenus} title="Refresh">
                    <span className="k-icon k-font-icon k-i-reload" />
                </button>
            </th>
        );
    }, [onRefreshClick, state.loadingRoles, state.loadingMenus]);

    const HamburgerCell = useCallback((props: GridCellProps) => {
        const dataItem = props.dataItem as UserRoleRow;
        const inEdit = state.editRoleKey === dataItem.roleKey;
        return (
            <td style={{ textAlign: 'center', padding: '2px' }}>
                {inEdit ? (
                    <button
                        className="k-button k-button-sm k-rounded-md k-button-solid-success"
                        title="Save"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(dataItem.roleKey, e);
                            handleSave();
                        }}
                    >
                        <span className="k-icon k-font-icon k-i-check" />
                    </button>
                ) : (
                    <button
                        className="k-button k-button-sm k-rounded-md"
                        title="Actions"
                        onClick={(e) => {
                            e.stopPropagation();

setState(prev => ({
    ...prev,
    selectedRoleKey: dataItem.roleKey,
}));

                            openRowMenu(dataItem, e.currentTarget);
                        }}
                    >
                        <span className="k-icon k-font-icon k-i-more-vertical" />
                    </button>
                )}
            </td>
        );
    }, [state.editRoleKey, handleRowClick, handleSave, openRowMenu]);

    const CommandCell = useCallback((props: GridCellProps) => {
        const dataItem = props.dataItem as UserRoleRow;
        const inEdit = state.editRoleKey === dataItem.roleKey;
        return (
            <td className="packlists-command-cell tmi-command-column tmi-command-cell">
                {inEdit ? (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-secondary tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.roleKey, e); handleCancel(); }} title="Cancel">
                            <span className="k-icon k-font-icon k-i-cancel" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.roleKey, e); openDeleteConfirm(dataItem); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                ) : (
                    <>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-base tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleRowClick(dataItem.roleKey, e); handleEnterEdit(dataItem); }} title="Edit">
                            <span className="k-icon k-font-icon k-i-edit" />
                        </button>
                        <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-next" onClick={(e) => { handleRowClick(dataItem.roleKey, e); openDeleteConfirm(dataItem); }} title="Delete">
                            <span className="k-icon k-font-icon k-i-trash" />
                        </button>
                    </>
                )}
            </td>
        );
    }, [state.editRoleKey, handleRowClick, handleCancel, openDeleteConfirm, handleEnterEdit]);

    const CommandHeaderCell = useCallback(() => {
        return (
            <th className="tmi-command-header-cell tmi-command-column">
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                    title="New User Role"
                    onClick={openNewDialog}
                >
                    <span className="k-icon k-font-icon k-i-plus" />
                </button>
            </th>
        );
    }, [openNewDialog]);

    const renderTreeNode = useCallback((treeNode: TreeNode, depth: number = 0): JSX.Element => {
        const row = treeNode.node;
        const label = row.description || row.securityLabel || '(Unnamed Menu)';
        const isChecked = state.checkedMenuIds.has(row.securityID);
        const hasChildren = treeNode.children.length > 0;
        const isExpanded = !state.collapsedMenuIds.has(row.securityID);

        return (
            <li key={`${row.securityID}_${depth}`} className="user-roles-tree-node">
                <div className={`user-roles-tree-row ${row.enable ? '' : 'is-disabled'}`} style={{ paddingLeft: `${depth * 14}px` }}>
                    {hasChildren ? (
                        <button
                            type="button"
                            className="user-roles-tree-expander"
                            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label}`}
                            title={isExpanded ? 'Collapse' : 'Expand'}
                            onClick={(e) => {
                                e.stopPropagation();
                                setState(prev => {
                                    const nextCollapsed = new Set(prev.collapsedMenuIds);
                                    if (nextCollapsed.has(row.securityID)) {
                                        nextCollapsed.delete(row.securityID);
                                    } else {
                                        nextCollapsed.add(row.securityID);
                                    }
                                    return { ...prev, collapsedMenuIds: nextCollapsed };
                                });
                            }}
                        >
                            <span className={`k-icon k-font-icon ${isExpanded ? 'k-i-caret-alt-down' : 'k-i-caret-alt-right'}`} />
                        </button>
                    ) : (
                        <span className="user-roles-tree-expander-spacer" aria-hidden="true" />
                    )}
                    <input
                        type="checkbox"
                        className="user-roles-tree-checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                            const nextChecked = e.target.checked;
                            setState(prev => {
                                const hiddenCheckedIds = new Set<number>();
                                const visibleCheckedIds = new Set<number>();

                                prev.checkedMenuIds.forEach(id => {
                                    if (visibleSecurityIds.has(id)) {
                                        visibleCheckedIds.add(id);
                                    } else {
                                        hiddenCheckedIds.add(id);
                                    }
                                });

                                if (nextChecked) {
                                    visibleCheckedIds.add(row.securityID);
                                } else {
                                    visibleCheckedIds.delete(row.securityID);
                                }

                                const nextSet = new Set<number>([...hiddenCheckedIds, ...visibleCheckedIds]);

                                const selectedRole = prev.rolesData.find(r => r.roleKey === prev.selectedRoleKey);
                                const userRoleID = selectedRole ? Number(selectedRole.value) : NaN;
                                if (Number.isInteger(userRoleID) && userRoleID >= 0) {
                                    saveCheckedSecuritiesForRole(userRoleID, nextSet);
                                }

                                return { ...prev, checkedMenuIds: nextSet };
                            });
                        }}
                    />
                    <span className="user-roles-tree-id">{row.securityID}</span>
                    <span className="user-roles-tree-label" title={label}>{label}</span>
                </div>

                {hasChildren && isExpanded && (
                    <ul className="user-roles-tree-children">
                        {treeNode.children.map(child => renderTreeNode(child, depth + 1))}
                    </ul>
                )}
            </li>
        );
    }, [state.checkedMenuIds, state.collapsedMenuIds, saveCheckedSecuritiesForRole, visibleSecurityIds]);

    const handleUsersByRoleRowClick = useCallback((clientRowKey: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setState(prev => ({ ...prev, selectedUserByRoleKey: clientRowKey }));
    }, []);

    const UsersByRoleTextCell = useCallback((props: GridCellProps) => {
        const row = props.dataItem as UsersByRoleRow;
        const field = props.field as 'value' | 'display';
        const isSelected = state.selectedUserByRoleKey === row.clientRowKey;
        const val = (row[field] ?? '') as string;
        return (
            <td
                className={`tmi-dialog-grid-text-cell ${isSelected ? 'tmi-dialog-grid-cell-selected' : ''}`}
                onClick={(e) => handleUsersByRoleRowClick(row.clientRowKey, e)}
                style={{ cursor: 'pointer' }}
                title={String(val)}
            >
                {val}
            </td>
        );
    }, [state.selectedUserByRoleKey, handleUsersByRoleRowClick]);

    const openUsersDeleteConfirm = useCallback((row: UsersByRoleRow) => {
        setState(prev => ({ ...prev, usersDeleteConfirmItem: row }));
    }, []);

    const closeUsersDeleteConfirm = useCallback(() => {
        setState(prev => ({ ...prev, usersDeleteConfirmItem: null }));
    }, []);

    const confirmUsersDelete = useCallback(() => {
        const deleting = state.usersDeleteConfirmItem;
        if (!deleting) return;

        const selectedRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        const userRoleID = selectedRole ? Number(selectedRole.value) : NaN;
        const userID = Number(deleting.value);

        if (!Number.isInteger(userRoleID) || userRoleID < 0 || !Number.isInteger(userID) || userID < 0) {
            setState(prev => ({
                ...prev,
                errorMsg: 'Unable to delete user from role: invalid role or user ID.',
            }));
            return;
        }

        const url = `${props.userInf.currProject.apiUrl}/api/TMI/DeleteUserByUserRole?userRoleID=${userRoleID}&userID=${userID}`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, usersDeleteConfirmItem: null }));
                    return;
                }

                if (res.callStatus !== 'OK') {
                    setState(prev => ({
                        ...prev,
                        errorMsg: 'Error deleting user from role: ' + (res.callStatusMessage || ''),
                    }));
                    return;
                }

                setState(prev => ({ ...prev, usersDeleteConfirmItem: null }));
                fetchUsersByRole(userRoleID);
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    errorMsg: 'Error deleting user from role: ' + err.toString(),
                }));
            });
    }, [state.usersDeleteConfirmItem, state.rolesData, state.selectedRoleKey, props.userInf, props.updateUserInfo, fetchUsersByRole]);

    const openUsersAddDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            usersAddDialogOpen: true,
            usersAddDialogLoading: true,
            usersAddDialogSaving: false,
            usersAddDialogFilterText: '',
            usersAddDialogData: [],
            usersAddDialogSelected: new Set<string>(),
            usersAddDialogError: '',
        }));

        const url = `${props.userInf.currProject.apiUrl}/api/TMI/GetUsersForDropDown`;
        fetch(url, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + props.userInf.token,
            },
        })
            .then(resp => resp.json())
            .then(res => {
                if (res.callStatus === 'UNAUTH') {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, usersAddDialogLoading: false }));
                    return;
                }

                if (res.callStatus !== 'OK') {
                    setState(prev => ({
                        ...prev,
                        usersAddDialogLoading: false,
                        usersAddDialogError: 'Error loading users: ' + (res.callStatusMessage || ''),
                    }));
                    return;
                }

                const existingUserIds = new Set(state.usersByRoleData.map(u => u.value));
                const dropdownData: LookupListItem[] = Array.isArray(res.results)
                    ? res.results
                        .map((item: any) => ({
                            value: String(item.value ?? item.Value ?? item.userID ?? item.UserID ?? ''),
                            display: String(item.display ?? item.Display ?? item.userName ?? item.UserName ?? ''),
                        }))
                        .filter((item: LookupListItem) => item.value !== '' && !existingUserIds.has(item.value))
                    : [];

                setState(prev => ({
                    ...prev,
                    usersAddDialogLoading: false,
                    usersAddDialogData: dropdownData,
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    usersAddDialogLoading: false,
                    usersAddDialogError: 'Error loading users: ' + err.toString(),
                }));
            });
    }, [props.userInf, props.updateUserInfo, state.usersByRoleData]);

    const closeUsersAddDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            usersAddDialogOpen: false,
            usersAddDialogLoading: false,
            usersAddDialogSaving: false,
            usersAddDialogFilterText: '',
            usersAddDialogData: [],
            usersAddDialogSelected: new Set<string>(),
            usersAddDialogError: '',
        }));
    }, []);

    const submitUsersAdd = useCallback(() => {
        const selectedRole = state.rolesData.find(r => r.roleKey === state.selectedRoleKey);
        const userRoleID = selectedRole ? Number(selectedRole.value) : NaN;
        if (!Number.isInteger(userRoleID) || userRoleID < 0) {
            setState(prev => ({ ...prev, usersAddDialogError: 'Please select a valid user role first.' }));
            return;
        }

        const selectedUsers = Array.from(state.usersAddDialogSelected)
            .map(v => Number(v))
            .filter(v => Number.isInteger(v) && v > 0);

        if (selectedUsers.length === 0) {
            setState(prev => ({ ...prev, usersAddDialogError: 'Select at least one user to add.' }));
            return;
        }

        setState(prev => ({ ...prev, usersAddDialogSaving: true, usersAddDialogError: '' }));

        const saveRequests = selectedUsers.map(userID => {
            const url = `${props.userInf.currProject.apiUrl}/api/TMI/AddUserByUserRole?userRoleID=${userRoleID}&userID=${userID}`;
            return fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.userInf.token,
                },
            }).then(resp => resp.json());
        });

        Promise.all(saveRequests)
            .then(results => {
                const unauth = results.find(r => r.callStatus === 'UNAUTH');
                if (unauth) {
                    const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                    props.updateUserInfo(uInf);
                    setState(prev => ({ ...prev, usersAddDialogSaving: false }));
                    return;
                }

                const failed = results.find(r => r.callStatus !== 'OK');
                if (failed) {
                    setState(prev => ({
                        ...prev,
                        usersAddDialogSaving: false,
                        usersAddDialogError: 'Error adding user(s): ' + (failed.callStatusMessage || ''),
                    }));
                    return;
                }

                fetchUsersByRole(userRoleID);
                setState(prev => ({
                    ...prev,
                    usersAddDialogOpen: false,
                    usersAddDialogLoading: false,
                    usersAddDialogSaving: false,
                    usersAddDialogFilterText: '',
                    usersAddDialogData: [],
                    usersAddDialogSelected: new Set<string>(),
                    usersAddDialogError: '',
                }));
            })
            .catch(err => {
                setState(prev => ({
                    ...prev,
                    usersAddDialogSaving: false,
                    usersAddDialogError: 'Error adding user(s): ' + err.toString(),
                }));
            });
    }, [state.rolesData, state.selectedRoleKey, state.usersAddDialogSelected, props.userInf, props.updateUserInfo, fetchUsersByRole]);

    const toggleUsersAddSelection = useCallback((userValue: string) => {
        setState(prev => {
            const nextSelected = new Set(prev.usersAddDialogSelected);
            if (nextSelected.has(userValue)) {
                nextSelected.delete(userValue);
            } else {
                nextSelected.add(userValue);
            }
            return { ...prev, usersAddDialogSelected: nextSelected };
        });
    }, []);

    const handleUsersAddFilterChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setState(prev => ({ ...prev, usersAddDialogFilterText: e.target.value }));
    }, []);

    const UsersByRoleCommandCell = useCallback((props: GridCellProps) => {
        const dataItem = props.dataItem as UsersByRoleRow;
        return (
            <td className="packlists-command-cell tmi-command-column tmi-command-cell">
                <button className="k-button k-button-sm k-rounded-md k-button-solid-error tmi-dialog-action-button tmi-dialog-action-button-first" onClick={(e) => { handleUsersByRoleRowClick(dataItem.clientRowKey, e); openUsersDeleteConfirm(dataItem); }} title="Delete">
                    <span className="k-icon k-font-icon k-i-trash" />
                </button>
            </td>
        );
    }, [handleUsersByRoleRowClick, openUsersDeleteConfirm]);

    const UsersByRoleCommandHeaderCell = useCallback(() => {
        return (
            <th className="tmi-command-header-cell tmi-command-column">
                <button
                    className="k-button k-button-sm k-rounded-md k-button-solid-primary"
                    title="New User"
                    disabled={!state.selectedRoleKey}
                    onClick={openUsersAddDialog}
                >
                    <span className="k-icon k-font-icon k-i-plus" />
                </button>
            </th>
        );
    }, [openUsersAddDialog, state.selectedRoleKey]);

    const [securityDetailExpanded, setSecurityDetailExpanded] = useState<Set<string>>(() => {
        const initial = new Set<string>();
        const addAllIds = (nodes: SecurityDetailTreeNode[]) => {
            nodes.forEach(node => {
                if (node.children.length > 0) {
                    initial.add(node.node.securityDetailID);
                    addAllIds(node.children);
                }
            });
        };
        addAllIds(buildSecurityDetailTree(state.securityDetailData));
        return initial;
    });

    useEffect(() => {
        setSecurityDetailExpanded(prev => {
            const next = new Set<string>();
            const addAllIds = (nodes: SecurityDetailTreeNode[]) => {
                nodes.forEach(node => {
                    if (node.children.length > 0) {
                        next.add(node.node.securityDetailID);
                        addAllIds(node.children);
                    }
                });
            };
            addAllIds(buildSecurityDetailTree(state.securityDetailData));
            return next;
        });
    }, [state.securityDetailData]);

    const toggleSecurityDetailNode = useCallback((id: string) => {
        setSecurityDetailExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    }, []);

    const securityDetailRoots = useMemo(() => buildSecurityDetailTree(state.securityDetailData), [state.securityDetailData]);
    const securityDetailCopyButtonText = state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType
        ? 'Copy Security Details from Selected to Checked Item Types'
        : 'Copy Security Details from Selected to Checked PackList Types';
    const securityDetailCopyButtonDisabled = !state.securityDetailListSelectedRow
        || state.securityDetailListSelected.size === 0
        || state.securityDetailListSelected.has(state.securityDetailListSelectedRow);
    const securityDetailCopyFromRoleButtonText = state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType
        ? 'Copy Security Details from another User Role to Checked Item Types'
        : 'Copy Security Details from another User Role to Checked PackList Types';
    const securityDetailCopyFromRoleButtonDisabled = state.securityDetailListSelected.size === 0;
    const availableSecurityDetailSourceRoles = useMemo(() => {
        if (!state.selectedRoleKey) {
            return [];
        }

        return state.rolesData.filter(role => role.roleKey !== state.selectedRoleKey && role.display.trim() !== '');
    }, [state.rolesData, state.selectedRoleKey]);

    const openSecurityDetailSourceRoleDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            securityDetailSourceRoleDialogOpen: true,
            securityDetailSourceRoleFilterText: '',
            securityDetailSourceRoleSelected: null,
            securityDetailDialogError: '',
        }));
    }, []);

    const closeSecurityDetailSourceRoleDialog = useCallback(() => {
        setState(prev => ({
            ...prev,
            securityDetailSourceRoleDialogOpen: false,
            securityDetailSourceRoleFilterText: '',
            securityDetailSourceRoleSelected: null,
        }));
    }, []);

    return (
        <div className="packlists-container user-roles-page">
            {state.errorMsg && <div className="packlists-error">{state.errorMsg}</div>}

            <div className="user-roles-layout">
                <section className="user-roles-grid-panel">
                    <div className="packlists-header-top user-roles-panel-header">
                        <h3>User Roles</h3>
                        {(state.loadingRoles || state.loadingMenus) && <span className="k-icon k-font-icon k-i-loading" />}
                    </div>

                    <Grid
                        className="tmi-grid MasterUsers-grid"
                        style={{ width: '100%', flex: 1, minHeight: 0 }}
                        data={rolesView}
                        dataItemKey="roleKey"
                        resizable
                    >
                        <GridColumn headerCell={HamburgerHeaderCell} cell={HamburgerCell} width={40} filterable={false} sortable={false} headerClassName="wrap-header" />
                        <GridColumn field="value" title="Role ID" width={100} headerCell={buildHeaderCellRenderer('value', 'Role ID')} cell={TextCell} headerClassName="wrap-header" resizable={true} />
                        <GridColumn field="display" title="Role" headerCell={buildHeaderCellRenderer('display', 'Role')} cell={TextCell} headerClassName="wrap-header" resizable={true} />
                        <GridColumn headerCell={CommandHeaderCell} cell={CommandCell} width={80} filterable={false} sortable={false} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
                    </Grid>

                    {state.menuDataItem && state.menuPosition && (
                        <div className="packlists-menu-overlay" onClick={closeRowMenu}>
                            <div
                                ref={menuRef}
                                onClick={(e) => e.stopPropagation()}
                                style={{
                                    position: 'fixed',
                                    left: `${state.menuPosition.left}px`,
                                    top: `${state.menuPosition.top}px`,
                                    backgroundColor: 'white',
                                    border: '1px solid #e0e0e0',
                                    borderRadius: '4px',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                    zIndex: 10000,
                                    minWidth: '200px',
                                }}
                            >
                                <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0', minWidth: '200px' }}>
                                    <button
                                        className="k-button k-button-md packlists-menu-button"
                                        title="Set this user role as read only"
                                        onClick={() => {
                                            setUserRoleReadOnly();
                                        }}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '8px 16px',
                                            textAlign: 'left',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        Set User Role as Read Only
                                    </button>
                                    <button
                                        className="k-button k-button-md packlists-menu-button"
                                        title="Impersonate this role"
                                        onClick={() => {
                                            const appRef = (window as any).appRef;
                                            if (appRef && typeof appRef.impersonateUserRole === 'function') {
                                                appRef.impersonateUserRole(state.menuDataItem!.value);
                                            }
                                            closeRowMenu();
                                        }}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '8px 16px',
                                            textAlign: 'left',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            borderTop: '1px solid #e0e0e0',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        Impersonate Role
                                    </button>
                                    <button
                                        className="k-button k-button-md packlists-menu-button"
                                        title="View item type security details"
                                        onClick={() => {
                                            openSecurityDetailDialog(SecurityDetailDialogMode.ItemType);
                                        }}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '8px 16px',
                                            textAlign: 'left',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            borderTop: '1px solid #e0e0e0',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        ItemType Security Details
                                    </button>
                                    <button
                                        className="k-button k-button-md packlists-menu-button"
                                        title="View packlist type security details"
                                        onClick={() => {
                                            openSecurityDetailDialog(SecurityDetailDialogMode.PacklistType);
                                        }}
                                        style={{
                                            display: 'block',
                                            width: '100%',
                                            padding: '8px 16px',
                                            textAlign: 'left',
                                            border: 'none',
                                            backgroundColor: 'transparent',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            borderTop: '1px solid #e0e0e0',
                                        }}
                                        onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                        onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                                    >
                                        PackList Type Security Details
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {state.securityDetailDialogOpen && (
                        <Dialog title={state.securityDetailDialogTitle} onClose={closeSecurityDetailDialog} width={820}>
                            <div style={{ display: 'flex', gap: '16px', minHeight: '420px', maxHeight: '560px', padding: '8px 0 0' }}>
                                <div className="tmi-panel-box" style={{ width: '260px', minWidth: '260px',  display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ fontWeight: 600, fontSize: '13px', color: '#2d2d2d', background: '#f2f4f7',  padding: '6px 8px', borderBottom: '1px solid #d9d9d9' }}>
                                        {state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType ? 'Item Types' : 'Packlist Types'}
                                    </div>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.securityDetailListFilterText}
                                        onChange={(e) => setState(prev => ({ ...prev, securityDetailListFilterText: e.target.value }))}
                                        placeholder="Filter..."
                                        style={{ marginBottom: '8px' }}
                                    />
                                    <div style={{ overflow: 'auto', flex: 1 }}>
                                        {state.securityDetailListLoading ? (
                                            <div style={{ color: '#555', padding: '8px 0' }}>Loading...</div>
                                        ) : state.securityDetailListData.filter(item => item.display.toLowerCase().includes(state.securityDetailListFilterText.toLowerCase())).length === 0 ? (
                                            <div style={{ color: '#555', padding: '8px 0' }}>No records found.</div>
                                        ) : (
                                            state.securityDetailListData
                                                .filter(item => item.display.toLowerCase().includes(state.securityDetailListFilterText.toLowerCase()))
                                                .map(item => {
                                                    const isSelected = state.securityDetailListSelectedRow === item.value;
                                                    return (
                                                        <div
                                                            key={item.value}
                                                            onClick={() => {
                                                                selectSecurityDetailListRow(item.value);
                                                                loadSecurityDetailAllowEditForSelectedType(item.value);
                                                            }}
                                                            style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '8px',
                                                                padding: '3px 8px',
                                                                fontSize: '13px',
                                                                color: isSelected ? '#ffffff' : '#1f1f1f',
                                                                borderRadius: '4px',
                                                                border: isSelected ? '1px solid #FF9800' : '1px solid transparent',
                                                                background: isSelected ? '#FF9800' : 'transparent',
                                                                cursor: 'pointer',
                                                            }}
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={state.securityDetailListSelected.has(item.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleSecurityDetailListSelection(item.value);
                                                                }}
                                                            />
                                                            <span>{item.display}</span>
                                                        </div>
                                                    );
                                                })
                                        )}
                                    </div>
                                </div>

                                <div className="tmi-panel-box" style={{ flex: 1, minWidth: 0, maxHeight: '560px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ overflow: 'auto', flex: 1, padding: '8px' }}>
                                        {state.securityDetailDialogLoading ? (
                                            <div style={{ padding: '12px 0', color: '#555' }}>Loading security details...</div>
                                        ) : state.securityDetailDialogError ? (
                                            <div style={{ color: '#d32f2f', padding: '12px 0' }}>{state.securityDetailDialogError}</div>
                                        ) : (
                                            <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                {securityDetailRoots.length === 0 ? (
                                                    <div style={{ padding: '12px 0', color: '#555' }}>No security details available.</div>
                                                ) : (
                                                    <>
                                                        <div style={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto auto', alignItems: 'center', gap: '6px', padding: '4px 8px', borderBottom: '1px solid #d9d9d9', position: 'sticky', top: 0, background: '#f2f4f7', zIndex: 1, margin: '-8px -8px 4px -8px' }}>
                                                            <span style={{ width: '18px' }} />
                                                            <div style={{ fontWeight: 600, fontSize: '12px', color: '#2d2d2d' }}>Security Detail</div>
                                                            <div style={{ fontWeight: 600, fontSize: '12px', color: '#2d2d2d', textAlign: 'center', minWidth: '88px' }}>Allow Access</div>
                                                            <div style={{ fontWeight: 600, fontSize: '12px', color: '#2d2d2d', textAlign: 'center', minWidth: '90px' }}>Allow Edit</div>
                                                        </div>
                                                        {securityDetailRoots.map((root, rootIndex) => (
                                                            <div key={root.node.securityDetailID || rootIndex}>
                                                                {renderSecurityDetailNode(root, 0, securityDetailExpanded, toggleSecurityDetailNode, state.securityDetailAllowAccessMap, state.securityDetailAllowEditMap, updateSecurityDetailAllowAccess, updateSecurityDetailAllowEdit)}
                                                            </div>
                                                        ))}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {state.securityDetailDialogStatus && (
                                <div
                                    role="status"
                                    style={{
                                        color: state.securityDetailDialogStatus.type === 'success' ? '#2e7d32' : '#d32f2f',
                                        backgroundColor: state.securityDetailDialogStatus.type === 'success' ? '#edf7ed' : '#ffebee',
                                        border: `1px solid ${state.securityDetailDialogStatus.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`,
                                        borderRadius: '4px',
                                        padding: '8px 10px',
                                        marginTop: '12px',
                                        fontSize: '13px',
                                    }}
                                >
                                    {state.securityDetailDialogStatus.message}
                                </div>
                            )}
                            {state.securityDetailCopying && (
                                <div role="status" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', color: '#555', fontSize: '13px' }}>
                                    <span className="k-icon k-font-icon k-i-loading" />
                                    Copying security details...
                                </div>
                            )}
                            <DialogActionsBar>
                                <button
                                    type="button"
                                    className="k-button k-button-md k-rounded-md k-button-solid-primary"
                                    onClick={copySecurityDetailSelectedToChecked}
                                    disabled={securityDetailCopyButtonDisabled || state.securityDetailCopying}
                                    title={securityDetailCopyButtonDisabled ? 'Select a different type and check at least one type before copying.' : securityDetailCopyButtonText}
                                >
                                    {securityDetailCopyButtonText}
                                </button>
                                <button
                                    type="button"
                                    className="k-button k-button-md k-rounded-md k-button-solid-secondary"
                                    onClick={openSecurityDetailSourceRoleDialog}
                                    disabled={securityDetailCopyFromRoleButtonDisabled || state.securityDetailCopying}
                                    title={securityDetailCopyFromRoleButtonDisabled ? 'Check at least one type before copying from another user role.' : securityDetailCopyFromRoleButtonText}
                                >
                                    {securityDetailCopyFromRoleButtonText}
                                </button>
                                <button type="button" className="k-button k-button-md k-rounded-md" onClick={closeSecurityDetailDialog}>Close</button>
                            </DialogActionsBar>
                        </Dialog>
                    )}

                    {state.securityDetailSourceRoleDialogOpen && (
                        <Dialog title={state.securityDetailDialogMode === SecurityDetailDialogMode.ItemType ? 'Copy Item Type Security Details from another User Role' : 'Copy PackList Type Security Details from another User Role'} onClose={closeSecurityDetailSourceRoleDialog} width={420}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0' }}>
                                <input
                                    type="text"
                                    className="k-input tmi-dialog-list-filter"
                                    placeholder="Filter user roles..."
                                    value={state.securityDetailSourceRoleFilterText}
                                    onChange={(e) => setState(prev => ({ ...prev, securityDetailSourceRoleFilterText: e.target.value }))}
                                />
                                <div className="tmi-dialog-list-container">
                                    {availableSecurityDetailSourceRoles.length === 0 && (
                                        <div className="tmi-dialog-list-empty">No other user roles available</div>
                                    )}
                                    {availableSecurityDetailSourceRoles
                                        .filter(role => role.display.toLowerCase().includes(state.securityDetailSourceRoleFilterText.toLowerCase()))
                                        .map((role) => (
                                            <div key={role.roleKey} className="tmi-dialog-list-item">
                                                <input
                                                    type="radio"
                                                    name="security-detail-source-role"
                                                    checked={state.securityDetailSourceRoleSelected === role.roleKey}
                                                    onChange={() => setState(prev => ({ ...prev, securityDetailSourceRoleSelected: role.roleKey }))}
                                                    id={`security-detail-source-role-${role.roleKey}`}
                                                />
                                                <label htmlFor={`security-detail-source-role-${role.roleKey}`} style={{ marginLeft: '8px', cursor: 'pointer' }}>
                                                    {role.display}
                                                </label>
                                            </div>
                                        ))}
                                </div>
                            </div>
                            <DialogActionsBar>
                                <button type="button" className="k-button k-button-md k-rounded-md" onClick={closeSecurityDetailSourceRoleDialog}>Cancel</button>
                                <button
                                    type="button"
                                    className="k-button k-button-md k-rounded-md k-button-solid-primary"
                                    onClick={copySecurityDetailFromRoleToChecked}
                                    disabled={!state.securityDetailSourceRoleSelected || state.securityDetailCopying}
                                >
                                    {state.securityDetailCopying && <span className="k-icon k-font-icon k-i-loading" style={{ marginRight: '6px' }} />}
                                    Select Role
                                </button>
                            </DialogActionsBar>
                        </Dialog>
                    )}

                    <ProjectSelectionDialog
                        open={state.copyToProjectDialogOpen}
                        title="Select Project to Copy To"
                        apiUrl={props.userInf.currProject.apiUrl}
                        token={props.userInf.token}
                        emptyMessage="No projects available to copy to"
                        onClose={closeCopyToProjectDialog}
                        onSelectProject={handleCopyToProjectSelection}
                    />

                    {state.copyToProjectNameDialogOpen && state.copyToProjectTarget && (
                        <Dialog title={`Copy Role to ${state.copyToProjectTarget.display}`} onClose={closeCopyToProjectNameDialog} width={420}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '16px 0', minWidth: '320px' }}>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontWeight: 'bold' }}>
                                    New User Role Name
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.copyToProjectName}
                                        onChange={(e) => setState(prev => ({ ...prev, copyToProjectName: e.target.value, copyToProjectError: '' }))}
                                        placeholder="Enter new user role name"
                                    />
                                </label>

                                {state.copyToProjectError && (
                                    <div className="packlists-error">{state.copyToProjectError}</div>
                                )}
                            </div>

                            <DialogActionsBar>
                                <button type="button" className="k-button k-button-md k-rounded-md" onClick={closeCopyToProjectNameDialog}>Cancel</button>
                                <button type="button" className="k-button k-button-md k-rounded-md k-button-solid-primary" onClick={submitCopyToProjectRole}>
                                    Copy Role
                                </button>
                            </DialogActionsBar>
                        </Dialog>
                    )}

                    {state.copyToProjectConfirmOpen && state.copyToProjectTarget && (
                        <Dialog title="User Role Already Exists" onClose={reopenCopyToProjectNameDialog} width={420}>
                            <div style={{ padding: '16px 0' }}>
                                <p style={{ margin: 0 }}>
                                    A user role named <strong>{state.copyToProjectName}</strong> already exists in <strong>{state.copyToProjectTarget.display}</strong>.
                                </p>
                                <p style={{ margin: '12px 0 0 0' }}>
                                    Would you like to update the existing user role instead?
                                </p>
                            </div>
                            <DialogActionsBar>
                                <button type="button" className="k-button k-button-md k-rounded-md" onClick={reopenCopyToProjectNameDialog}>
                                    No, rename it
                                </button>
                                <button
                                    type="button"
                                    className="k-button k-button-md k-rounded-md k-button-solid-primary"
                                    onClick={async () => {
                                        const targetProject = state.copyToProjectTarget;
                                        if (!targetProject) {
                                            setState(prev => ({
                                                ...prev,
                                                copyToProjectConfirmOpen: false,
                                                copyToProjectDuplicateRole: null,
                                                copyToProjectError: 'Target project is no longer available.',
                                            }));
                                            return;
                                        }

                                        const sourceRole = state.copyToProjectRole ?? state.rolesData.find(r => r.roleKey === state.selectedRoleKey) ?? null;
                                        const sourceUserRoleID = sourceRole ? Number(sourceRole.value) : NaN;
                                        const updateUserRoleID = Number(state.copyToProjectDuplicateRole?.value ?? '');

                                        if (!Number.isInteger(updateUserRoleID) || updateUserRoleID < 0) {
                                            setState(prev => ({
                                                ...prev,
                                                copyToProjectConfirmOpen: false,
                                                copyToProjectDuplicateRole: null,
                                                copyToProjectError: 'Unable to determine the existing target role ID.',
                                            }));
                                            return;
                                        }

                                        if (!sourceRole || !Number.isInteger(sourceUserRoleID) || sourceUserRoleID < 0) {
                                            setState(prev => ({
                                                ...prev,
                                                copyToProjectConfirmOpen: false,
                                                copyToProjectDuplicateRole: null,
                                                copyToProjectError: 'Unable to determine the source user role.',
                                            }));
                                            return;
                                        }

                                        try {
                                            const url = `${props.userInf.currProject.apiUrl}/api/TMI/CopyUserRoleToProject?projectID=${Number(targetProject.value)}&sourceUserRoleID=${sourceUserRoleID}&updateUserRoleID=${updateUserRoleID}`;
                                            const res = await fetch(url, {
                                                method: 'GET',
                                                headers: {
                                                    'Content-Type': 'application/json',
                                                    Authorization: `Bearer ${props.userInf.token}`,
                                                },
                                            }).then(r => r.json());

                                            if (res.callStatus === 'UNAUTH') {
                                                const uInf: UserInfo = { ...props.userInf, isAuthorised: false };
                                                props.updateUserInfo(uInf);
                                                return;
                                            }

                                            if (res.callStatus !== 'OK') {
                                                setState(prev => ({
                                                    ...prev,
                                                    copyToProjectConfirmOpen: false,
                                                    copyToProjectDuplicateRole: null,
                                                    copyToProjectError: 'Error updating existing user role: ' + (res.callStatusMessage || 'Unknown error.'),
                                                }));
                                                return;
                                            }

                                            setState(prev => ({
                                                ...prev,
                                                copyToProjectConfirmOpen: false,
                                                copyToProjectDuplicateRole: null,
                                                copyToProjectError: '',
                                            }));
                                        } catch (err) {
                                            setState(prev => ({
                                                ...prev,
                                                copyToProjectConfirmOpen: false,
                                                copyToProjectDuplicateRole: null,
                                                copyToProjectError: 'Error updating existing user role: ' + (err as Error).toString(),
                                            }));
                                        }
                                    }}
                                >
                                    Yes, update existing
                                </button>
                            </DialogActionsBar>
                        </Dialog>
                    )}

                    {state.deleteConfirmItem && (
                        <Dialog title="Confirm Delete" onClose={closeDeleteConfirm}>
                            <p style={{ margin: '0 0 8px 0' }}>
                                Are you sure you want to delete <strong>{state.deleteConfirmItem.display || state.deleteConfirmItem.value}</strong>?
                            </p>
                            <DialogActionsBar>
                                <button className="k-button k-button-solid-error" onClick={confirmDelete}>Delete</button>
                                <button className="k-button k-button-solid-base" onClick={closeDeleteConfirm}>Cancel</button>
                            </DialogActionsBar>
                        </Dialog>
                    )}

                    {state.newDialogOpen && (
                        <Dialog title="New User Role" onClose={closeNewDialog}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', minWidth: '340px' }}>
                                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span>Role ID</span>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value="0"
                                        readOnly
                                    />
                                </label>

                                <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <span>Role</span>
                                    <input
                                        type="text"
                                        className="k-input k-input-md"
                                        value={state.newDialogDisplay}
                                        onChange={(e) => setState(prev => ({ ...prev, newDialogDisplay: e.target.value }))}
                                    />
                                </label>

                                {state.newDialogError && <div className="packlists-error">{state.newDialogError}</div>}
                            </div>

                            <DialogActionsBar>
                                <button className="k-button k-button-solid-primary" onClick={submitNewRole}>Create</button>
                                <button className="k-button k-button-solid-base" onClick={closeNewDialog}>Cancel</button>
                            </DialogActionsBar>
                        </Dialog>
                    )}
                </section>

                <section className="user-roles-hierarchy-panel">
                    <div className="user-roles-panel-header">
                        <h3>Menu Hierarchy</h3>
                        {state.loadingMenus && <span className="k-icon k-font-icon k-i-loading" />}
                    </div>

                    <div className="user-roles-hierarchy-content" ref={hierarchyContentRef}>

                        {visibleMenuTree.length === 0 && (
                            <div className="user-roles-empty-state">No menus found.</div>
                        )}

                        {visibleMenuTree.length > 0 && (
                            <ul className="user-roles-tree-root">
                                {visibleMenuTree.map(node => renderTreeNode(node))}
                            </ul>
                        )}
                    </div>
                </section>

                <section className="user-roles-users-panel">
                    <div className="user-roles-panel-header">
                        <h3>Users In Role</h3>
                        {state.loadingUsersByRole && <span className="k-icon k-font-icon k-i-loading" />}
                    </div>

                    <div className="user-roles-users-content">
                        {!state.selectedRoleKey && (
                            <div className="user-roles-empty-state">Select a role to view linked users.</div>
                        )}

                        {state.selectedRoleKey && (
                            <>
                                <Grid
                                    className="tmi-grid MasterUsers-grid user-roles-users-grid"
                                    style={{ width: '100%', minHeight: '260px' }}
                                    data={usersByRoleView}
                                    dataItemKey="clientRowKey"
                                    resizable
                                >
                                    <GridColumn field="value" title="User ID" width={120} headerCell={buildUsersByRoleHeaderCellRenderer('value', 'User ID')} cell={UsersByRoleTextCell} headerClassName="wrap-header" />
                                    <GridColumn field="display" title="User" headerCell={buildUsersByRoleHeaderCellRenderer('display', 'User')} cell={UsersByRoleTextCell} headerClassName="wrap-header" />
                                    <GridColumn headerCell={UsersByRoleCommandHeaderCell} cell={UsersByRoleCommandCell} width={50} filterable={false} sortable={false} locked={true} resizable={false} headerClassName="wrap-header tmi-command-column" />
                                </Grid>

                                {!state.loadingUsersByRole && usersByRoleView.length === 0 && (
                                    <div className="user-roles-empty-state" style={{ marginTop: '8px' }}>No users linked to this role.</div>
                                )}
                            </>
                        )}
                    </div>

                    {state.usersDeleteConfirmItem && (
                        <Dialog title="Confirm Delete" onClose={closeUsersDeleteConfirm}>
                            <p style={{ margin: '0 0 8px 0' }}>
                                Are you sure you want to delete user <strong>{state.usersDeleteConfirmItem.display || state.usersDeleteConfirmItem.value}</strong> from this role?
                            </p>
                            <DialogActionsBar>
                                <button className="k-button k-button-solid-error" onClick={confirmUsersDelete}>Delete</button>
                                <button className="k-button k-button-solid-base" onClick={closeUsersDeleteConfirm}>Cancel</button>
                            </DialogActionsBar>
                        </Dialog>
                    )}

                    {state.usersAddDialogOpen && (
                        <Dialog title="Add Users To Role" onClose={closeUsersAddDialog} width={500} height={600}>
                            <div className="tmi-dialog-list-container" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '8px 0', height: '460px' }}>
                                {state.usersAddDialogError && (
                                    <div className="tmi-dialog-list-error">
                                        {state.usersAddDialogError}
                                    </div>
                                )}
                                {state.usersAddDialogLoading ? (
                                    <div className="tmi-dialog-list-loading">
                                        <span className="k-icon k-font-icon k-i-loading" />
                                        Loading users...
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            className="k-input tmi-dialog-list-filter"
                                            placeholder="Filter users..."
                                            value={state.usersAddDialogFilterText}
                                            onChange={handleUsersAddFilterChange}
                                        />
                                        <div className="tmi-dialog-list-content" style={{ flex: 1, overflowY: 'auto' }}>
                                            {state.usersAddDialogData
                                                .filter(user => user.display.toLowerCase().includes(state.usersAddDialogFilterText.toLowerCase()))
                                                .map((user) => (
                                                    <div key={user.value} className="tmi-dialog-list-item">
                                                        <input
                                                            type="checkbox"
                                                            id={`users-add-${user.value}`}
                                                            checked={state.usersAddDialogSelected.has(user.value)}
                                                            onChange={() => toggleUsersAddSelection(user.value)}
                                                        />
                                                        <label htmlFor={`users-add-${user.value}`} style={{ marginLeft: '8px', cursor: 'pointer' }}>
                                                            {user.display}
                                                        </label>
                                                    </div>
                                                ))}
                                        </div>
                                    </>
                                )}
                            </div>

                            <DialogActionsBar>
                                <button className="k-button k-button-solid-primary" onClick={submitUsersAdd} disabled={state.usersAddDialogSaving || state.usersAddDialogLoading}>Add Selected</button>
                                <button className="k-button k-button-solid-base" onClick={closeUsersAddDialog} disabled={state.usersAddDialogSaving}>Cancel</button>
                            </DialogActionsBar>
                        </Dialog>
                    )}
                </section>
            </div>
        </div>
    );
};

export default connector(UserRoles);